import {
  ClearPathCommand,
  createLevelSnapshot,
  createLevelState,
  getCell,
  pointKey,
  restorePathCells,
  type GridPoint,
  type LevelSnapshot,
  type LevelState,
  type PathSnapshot
} from "@gridflow/core";
import {
  developmentLevelCatalog,
  getLevelById,
  getLevelByOrder,
  type LevelCatalog,
  type LevelCatalogEntry
} from "@gridflow/levels";
import { Application, Container, Graphics, Text } from "pixi.js";
import type { ColorSource, Ticker } from "pixi.js";
import {
  GridInputController,
  createWebVibrationHaptics,
  gridCellCenter,
  screenToGrid,
  type BoardGeometry,
  type HapticFeedback,
  type ScreenPoint
} from "./input";
import {
  InterstitialFrequencyGate,
  createMockAdService,
  type AdService,
  type AdStatusEvent,
  type ConsentStatus,
  type InterstitialFrequencyGateOptions,
  type RewardedAdResult
} from "./ads";
import {
  createMockAnalyticsService,
  shouldTrackLevelAbandoned,
  type AnalyticsFields,
  type LevelAbandonSource,
  type AnalyticsService,
  type AnalyticsEventName
} from "./analytics";
import { createBrowserAudioService, type AudioService } from "./audio";
import { createSolverHintService, type HintResult, type HintService } from "./hints";
import { createBrowserProgressService, type ProgressService } from "./progress";
import {
  StuckDetector,
  type StuckDetectorOptions,
  type StuckDetectorSnapshot
} from "./stuck-detector";
import {
  GRIDFLOW_TOKENS,
  resolvePathToken,
  type ColorVisionProfile,
  type NodeShape
} from "./tokens";

export interface GridFlowGameOptions {
  readonly background?: ColorSource;
  readonly resolution?: number;
  readonly bottomSafeInset?: number;
  readonly catalog?: LevelCatalog;
  readonly levelId?: string;
  readonly progressService?: ProgressService;
  readonly haptics?: HapticFeedback;
  readonly reducedMotion?: boolean;
  readonly useColorBlindPalette?: boolean;
  readonly colorVisionProfile?: ColorVisionProfile;
  readonly preserveDrawingBuffer?: boolean;
  readonly adService?: AdService;
  readonly hintService?: HintService;
  readonly hintStuckThresholdMs?: number;
  readonly forceHintAvailable?: boolean;
  readonly interstitialGateOptions?: InterstitialFrequencyGateOptions;
  readonly audioService?: AudioService;
  readonly analyticsService?: AnalyticsService;
}

export interface GridFlowGame {
  readonly app: Application;
  readonly canvas: HTMLCanvasElement;
  readonly currentLevelId: string;
  getSnapshot(): LevelSnapshot;
  loadLevel(levelId: string): void;
  resetLevel(): void;
  previousLevel(): void;
  nextLevel(): void;
  destroy(): void;
}

declare global {
  interface Window {
    render_game_to_text?: () => string;
    advanceTime?: (ms: number) => void;
  }
}

type ButtonId =
  | "about"
  | "haptics"
  | "hint"
  | "hintCancel"
  | "hintConfirm"
  | "language"
  | "music"
  | "next"
  | "palette"
  | "policy"
  | "privacy"
  | "previous"
  | "reset"
  | "settings"
  | "sound"
  | "undo";
export type ButtonIcon =
  | "about"
  | "close"
  | "haptics"
  | "hint"
  | "language"
  | "music"
  | "next"
  | "palette"
  | "policy"
  | "privacy"
  | "previous"
  | "reset"
  | "settings"
  | "sound"
  | "undo";
type SettingsPanel = "main" | "about";
export type GameLanguage = "system" | "en" | "zh-Hans" | "zh-Hant" | "es" | "ms" | "th" | "ru";
export type ResolvedGameLanguage = Exclude<GameLanguage, "system">;

interface ButtonHitbox {
  readonly id: ButtonId;
  readonly enabled: boolean;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

interface BoardMetrics {
  readonly geometry: BoardGeometry;
  readonly boardWidth: number;
  readonly boardHeight: number;
  readonly bottomSafeInset: number;
  readonly controlDock: ControlDockMetrics;
  readonly screenWidth: number;
  readonly screenHeight: number;
}

interface ControlDockMetrics {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly centerX: number;
  readonly centerY: number;
}

interface LevelUndoSnapshot {
  readonly levelId: string;
  readonly paths: readonly {
    readonly color: string;
    readonly cells: readonly GridPoint[];
  }[];
}

interface LineClearCandidate {
  readonly color: string;
  readonly startPoint: ScreenPoint;
}

export interface UiCopy {
  readonly about: string;
  readonly aboutBody: readonly string[];
  readonly aboutTitle: string;
  readonly back: string;
  readonly cancel: string;
  readonly colorAssist: string;
  readonly hint: string;
  readonly hintConfirmBody: string;
  readonly hintConfirmTitle: string;
  readonly haptics: string;
  readonly language: string;
  readonly level: string;
  readonly music: string;
  readonly next: string;
  readonly off: string;
  readonly on: string;
  readonly previousLevel: string;
  readonly privacyOptions: string;
  readonly privacyPolicy: string;
  readonly settings: string;
  readonly soundFx: string;
  readonly watchAd: string;
}

export type SettingsRowId =
  | "sound"
  | "music"
  | "language"
  | "haptics"
  | "palette"
  | "privacy"
  | "policy"
  | "previous"
  | "about";

export interface SettingsRow {
  readonly id: SettingsRowId;
  readonly icon: ButtonIcon;
  readonly label: string;
  readonly value: string;
  readonly enabled: boolean;
  readonly accent?: boolean;
}

export interface PreferenceStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
}

export interface HapticsPreferenceAdapter extends HapticFeedback {
  isEnabled(): boolean;
  setEnabled(enabled: boolean): void;
  toggleEnabled(): boolean;
}

interface CelebrationParticle {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  ageMs: number;
  ttlMs: number;
  radius: number;
  color: number;
}

export interface LevelTransitionSnapshot {
  readonly active: boolean;
  readonly progress: number;
  readonly easedProgress: number;
}

export interface CrossingEraseFeedback {
  readonly activeColor: string;
  readonly erasedColor: string;
  readonly anchor: GridPoint;
  readonly erasedCells: readonly GridPoint[];
}

interface CrossingEraseFeedbackEvent extends CrossingEraseFeedback {
  ageMs: number;
  ttlMs: number;
}

export type GhostGuideKind = "path" | "two-end-start" | "crossing-erase";

export interface GhostGuidePreview {
  readonly color: string;
  readonly path: readonly GridPoint[];
}

export interface GhostGuideSpec {
  readonly kind: GhostGuideKind;
  readonly color: string;
  readonly path: readonly GridPoint[];
  readonly preview?: GhostGuidePreview;
  readonly eraseAnchor?: GridPoint;
}

export type HintButtonStateKind = "hidden" | "locked" | "charging" | "ready" | "loading";

export interface HintButtonState {
  readonly kind: HintButtonStateKind;
  readonly enabled: boolean;
  readonly progressRatio: number;
}

const EARLY_GHOST_GUIDES: Readonly<Record<number, GhostGuideSpec>> = {
  1: {
    kind: "path",
    color: "blue",
    path: [
      { x: 0, y: 0 },
      { x: 1, y: 0 },
      { x: 2, y: 0 },
      { x: 3, y: 0 },
      { x: 4, y: 0 }
    ]
  },
  2: {
    kind: "path",
    color: "blue",
    path: [
      { x: 0, y: 0 },
      { x: 0, y: 1 },
      { x: 1, y: 1 },
      { x: 1, y: 0 }
    ]
  },
  5: {
    kind: "path",
    color: "red",
    path: [
      { x: 0, y: 0 },
      { x: 1, y: 0 },
      { x: 2, y: 0 },
      { x: 3, y: 0 },
      { x: 3, y: 1 },
      { x: 2, y: 1 }
    ]
  },
  6: {
    kind: "path",
    color: "blue",
    path: [
      { x: 0, y: 0 },
      { x: 1, y: 0 },
      { x: 1, y: 1 },
      { x: 2, y: 1 },
      { x: 2, y: 2 }
    ]
  },
  7: {
    kind: "two-end-start",
    color: "red",
    path: [
      { x: 1, y: 2 },
      { x: 0, y: 2 },
      { x: 0, y: 1 },
      { x: 0, y: 0 }
    ]
  },
  8: {
    kind: "crossing-erase",
    color: "blue",
    path: [
      { x: 0, y: 2 },
      { x: 1, y: 2 },
      { x: 1, y: 3 },
      { x: 1, y: 4 },
      { x: 2, y: 4 }
    ],
    preview: {
      color: "red",
      path: [
        { x: 0, y: 0 },
        { x: 1, y: 0 },
        { x: 1, y: 1 },
        { x: 1, y: 2 }
      ]
    },
    eraseAnchor: { x: 1, y: 2 }
  }
};

const RENDER_CONSTANTS = {
  boardPanelAlpha: 0.76,
  boardGlowAlpha: 0.16,
  cellFillAlpha: 0.28,
  cellStrokeAlpha: 0.42,
  gridDotAlpha: 0.74,
  nodeFillAlpha: 0.9,
  nodeGlowAlpha: 0.24,
  lineInactiveAlpha: 0.84,
  lineCompleteAlpha: 0.98,
  lineGlowAlpha: 0.38,
  celebrationFlashAlpha: 0.58,
  guideAlpha: 0.74,
  guideHoldRatio: 1.2,
  guideMinVisibleCells: 2,
  activeHeadAlpha: 0.95,
  disabledButtonAlpha: 0.28,
  enabledButtonAlpha: 0.94,
  completeOverlayAlpha: 0.1,
  pointerCoordinateFallbackSize: 1,
  finalLevelLoopOrder: 1,
  undoHistoryLimit: 24,
  clickClearMaxDistanceRatio: 0.22,
  footerColumns: 3,
  footerRows: 3,
  celebrationMinCellsPerParticle: 1,
  celebrationParticleTargetPerPath: 18,
  celebrationMaxParticles: 72,
  colorHashPrime: 31,
  topActionSize: 42,
  previousButtonSize: 42,
  primaryActionRadius: 31,
  primaryActionLabelGap: 40,
  hudPillHeight: 46,
  utilityButtonSize: 44,
  controlDockHeight: 104,
  controlDockMaxWidth: 292,
  dockButtonSize: 50,
  progressDotRadius: 4,
  progressDotGap: 18,
  completionPulseLoopCount: 2,
  controlDockWidth: 156,
  crossingEraseFeedbackTtlMs: 420,
  levelTransitionDurationMs: 350
} as const;
const GRIDFLOW_LANGUAGE_STORAGE_KEY = "gridflow.language.v1";
export const GRIDFLOW_HAPTICS_STORAGE_KEY = "gridflow.haptics.v1";
export const GRIDFLOW_PRIVACY_POLICY_URL = "https://minigamesdesign.com/privacy/gridflow";
const GAME_LANGUAGES = [
  "system",
  "en",
  "zh-Hans",
  "zh-Hant",
  "es",
  "ms",
  "th",
  "ru"
] as const satisfies readonly GameLanguage[];
const GRIDFLOW_VERSION_LABEL = "0.0.1";
const UI_COPY = {
  en: {
    about: "About",
    aboutBody: [
      "GridFlow: Logic Puzzle",
      "Exact-length path puzzle",
      `Version ${GRIDFLOW_VERSION_LABEL}`,
      "minigamesdesign.com"
    ],
    aboutTitle: "About",
    back: "Back",
    cancel: "Cancel",
    colorAssist: "Color Assist",
    hint: "HINT",
    hintConfirmBody: "Watch a short ad to reveal one correct path for this level.",
    hintConfirmTitle: "Use a hint?",
    haptics: "Haptics",
    language: "Language",
    level: "LEVEL",
    music: "Music",
    next: "NEXT",
    off: "Off",
    on: "On",
    previousLevel: "Previous Level",
    privacyOptions: "Privacy Options",
    privacyPolicy: "Privacy Policy",
    settings: "Settings",
    soundFx: "Sound FX",
    watchAd: "Watch Ad"
  },
  "zh-Hans": {
    about: "关于",
    aboutBody: [
      "GridFlow: Logic Puzzle",
      "精确长度连线益智游戏",
      `版本 ${GRIDFLOW_VERSION_LABEL}`,
      "minigamesdesign.com"
    ],
    aboutTitle: "关于",
    back: "返回",
    cancel: "取消",
    colorAssist: "色盲辅助",
    hint: "提示",
    hintConfirmBody: "观看一段短广告，解锁本关一条正确连线。",
    hintConfirmTitle: "使用提示？",
    haptics: "触觉反馈",
    language: "语言",
    level: "关卡",
    music: "背景音",
    next: "下一关",
    off: "关",
    on: "开",
    previousLevel: "上一关",
    privacyOptions: "隐私选项",
    privacyPolicy: "隐私政策",
    settings: "设置",
    soundFx: "音效",
    watchAd: "看广告"
  },
  "zh-Hant": {
    about: "關於",
    aboutBody: [
      "GridFlow: Logic Puzzle",
      "精確長度連線益智遊戲",
      `版本 ${GRIDFLOW_VERSION_LABEL}`,
      "minigamesdesign.com"
    ],
    aboutTitle: "關於",
    back: "返回",
    cancel: "取消",
    colorAssist: "色彩輔助",
    hint: "提示",
    hintConfirmBody: "觀看一段短廣告，解鎖本關一條正確連線。",
    hintConfirmTitle: "使用提示？",
    haptics: "觸覺回饋",
    language: "語言",
    level: "關卡",
    music: "背景音",
    next: "下一關",
    off: "關",
    on: "開",
    previousLevel: "上一關",
    privacyOptions: "隱私選項",
    privacyPolicy: "隱私政策",
    settings: "設定",
    soundFx: "音效",
    watchAd: "看廣告"
  },
  es: {
    about: "Acerca de",
    aboutBody: [
      "GridFlow: Logic Puzzle",
      "Rompecabezas de rutas de longitud exacta",
      `Versión ${GRIDFLOW_VERSION_LABEL}`,
      "minigamesdesign.com"
    ],
    aboutTitle: "Acerca de",
    back: "Atrás",
    cancel: "Cancelar",
    colorAssist: "Asistencia de color",
    hint: "PISTA",
    hintConfirmBody: "Mira un anuncio corto para revelar una ruta correcta de este nivel.",
    hintConfirmTitle: "¿Usar una pista?",
    haptics: "Vibración",
    language: "Idioma",
    level: "NIVEL",
    music: "Música",
    next: "SIGUIENTE",
    off: "No",
    on: "Sí",
    previousLevel: "Nivel anterior",
    privacyOptions: "Privacidad",
    privacyPolicy: "Política",
    settings: "Ajustes",
    soundFx: "Efectos",
    watchAd: "Ver anuncio"
  },
  ms: {
    about: "Tentang",
    aboutBody: [
      "GridFlow: Logic Puzzle",
      "Teka-teki laluan panjang tepat",
      `Versi ${GRIDFLOW_VERSION_LABEL}`,
      "minigamesdesign.com"
    ],
    aboutTitle: "Tentang",
    back: "Kembali",
    cancel: "Batal",
    colorAssist: "Bantuan Warna",
    hint: "PETUNJUK",
    hintConfirmBody: "Tonton iklan pendek untuk membuka satu laluan betul tahap ini.",
    hintConfirmTitle: "Guna petunjuk?",
    haptics: "Getaran",
    language: "Bahasa",
    level: "TAHAP",
    music: "Muzik",
    next: "SETERUSNYA",
    off: "Mati",
    on: "Hidup",
    previousLevel: "Tahap sebelumnya",
    privacyOptions: "Pilihan Privasi",
    privacyPolicy: "Polisi Privasi",
    settings: "Tetapan",
    soundFx: "Bunyi",
    watchAd: "Tonton Iklan"
  },
  th: {
    about: "เกี่ยวกับ",
    aboutBody: [
      "GridFlow: Logic Puzzle",
      "เกมปริศนาเส้นทางความยาวพอดี",
      `เวอร์ชัน ${GRIDFLOW_VERSION_LABEL}`,
      "minigamesdesign.com"
    ],
    aboutTitle: "เกี่ยวกับ",
    back: "กลับ",
    cancel: "ยกเลิก",
    colorAssist: "ช่วยแยกสี",
    hint: "คำใบ้",
    hintConfirmBody: "ดูโฆษณาสั้นเพื่อเปิดเส้นทางที่ถูกต้องหนึ่งเส้นในด่านนี้",
    hintConfirmTitle: "ใช้คำใบ้?",
    haptics: "สั่น",
    language: "ภาษา",
    level: "ด่าน",
    music: "เพลง",
    next: "ถัดไป",
    off: "ปิด",
    on: "เปิด",
    previousLevel: "ด่านก่อนหน้า",
    privacyOptions: "ตัวเลือกส่วนตัว",
    privacyPolicy: "นโยบายส่วนตัว",
    settings: "ตั้งค่า",
    soundFx: "เสียง",
    watchAd: "ดูโฆษณา"
  },
  ru: {
    about: "О приложении",
    aboutBody: [
      "GridFlow: Logic Puzzle",
      "Головоломка с точной длиной пути",
      `Версия ${GRIDFLOW_VERSION_LABEL}`,
      "minigamesdesign.com"
    ],
    aboutTitle: "О приложении",
    back: "Назад",
    cancel: "Отмена",
    colorAssist: "Помощь с цветом",
    hint: "ПОДСКАЗКА",
    hintConfirmBody: "Посмотрите короткую рекламу, чтобы открыть один правильный путь.",
    hintConfirmTitle: "Использовать подсказку?",
    haptics: "Вибро",
    language: "Язык",
    level: "УРОВЕНЬ",
    music: "Музыка",
    next: "ДАЛЕЕ",
    off: "Выкл.",
    on: "Вкл.",
    previousLevel: "Предыдущий",
    privacyOptions: "Приватность",
    privacyPolicy: "Политика",
    settings: "Настройки",
    soundFx: "Звук",
    watchAd: "Смотреть"
  }
} as const satisfies Record<ResolvedGameLanguage, UiCopy>;

export function getCompletionPulseDurationSeconds(): number {
  return GRIDFLOW_TOKENS.motion.completePulseSeconds * RENDER_CONSTANTS.completionPulseLoopCount;
}

export function isCompletionPulseActive(elapsedSeconds: number): boolean {
  return elapsedSeconds >= 0 && elapsedSeconds < getCompletionPulseDurationSeconds();
}

export function getLevelTransitionSnapshot(
  elapsedMs: number,
  reducedMotion: boolean
): LevelTransitionSnapshot {
  if (reducedMotion) {
    return { active: false, progress: 1, easedProgress: 1 };
  }
  const progress = clamp01(elapsedMs / RENDER_CONSTANTS.levelTransitionDurationMs);
  return {
    active: progress < 1,
    progress,
    easedProgress: easeOutCubic(progress)
  };
}

export function createCrossingEraseFeedback(
  previousPaths: readonly PathSnapshot[],
  nextPaths: readonly PathSnapshot[],
  activeColor: string | null
): CrossingEraseFeedback | null {
  if (!activeColor) {
    return null;
  }

  const nextPathByColor = new Map(nextPaths.map((path) => [path.color, path]));
  const activePath = nextPathByColor.get(activeColor);
  const activeCells = new Set(activePath?.cells.map(pointKey) ?? []);

  for (const previousPath of previousPaths) {
    if (previousPath.color === activeColor) {
      continue;
    }
    const nextPath = nextPathByColor.get(previousPath.color);
    if (!nextPath || previousPath.cells.length <= nextPath.cells.length) {
      continue;
    }
    const nextCells = new Set(nextPath.cells.map(pointKey));
    const erasedCells = previousPath.cells.filter((cell) => !nextCells.has(pointKey(cell)));
    if (erasedCells.length === 0) {
      continue;
    }
    const anchor = erasedCells.find((cell) => activeCells.has(pointKey(cell))) ?? erasedCells[0];
    if (!anchor) {
      continue;
    }
    return {
      activeColor,
      erasedColor: previousPath.color,
      anchor,
      erasedCells
    };
  }

  return null;
}

export function createGhostGuidePathForLevel(
  levelOrder: number,
  state: LevelState
): readonly GridPoint[] {
  return createGhostGuideSpecForLevel(levelOrder, state)?.path ?? [];
}

export function createGhostGuideSpecForLevel(
  levelOrder: number,
  state: LevelState
): GhostGuideSpec | null {
  const guide = EARLY_GHOST_GUIDES[levelOrder];
  if (!guide) {
    return null;
  }
  const pointsToValidate = [
    ...guide.path,
    ...(guide.preview?.path ?? []),
    ...(guide.eraseAnchor ? [guide.eraseAnchor] : [])
  ];
  for (const point of pointsToValidate) {
    if (!isGuidePointAvailable(point, state)) {
      return null;
    }
  }
  return cloneGhostGuideSpec(guide);
}

export function getHintButtonState(options: {
  readonly hasWon: boolean;
  readonly inFlight: boolean;
  readonly canShowRewardedHint: boolean;
  readonly detector: StuckDetectorSnapshot;
}): HintButtonState {
  if (options.hasWon) {
    return { kind: "hidden", enabled: false, progressRatio: 0 };
  }
  if (options.inFlight) {
    return { kind: "loading", enabled: false, progressRatio: 1 };
  }
  if (!options.canShowRewardedHint || !options.detector.isHintEligibleLevel) {
    return { kind: "locked", enabled: false, progressRatio: 0 };
  }
  if (options.detector.isHintAvailable) {
    return { kind: "ready", enabled: true, progressRatio: 1 };
  }
  return {
    kind: "charging",
    enabled: false,
    progressRatio: clamp01(options.detector.progressRatio)
  };
}

export function shouldOpenRewardedHintConfirmation(options: {
  readonly confirmationOpen: boolean;
  readonly hintButtonState: HintButtonState;
}): boolean {
  return (
    !options.confirmationOpen &&
    options.hintButtonState.enabled &&
    options.hintButtonState.kind === "ready"
  );
}

export function getSettingsRows(options: {
  readonly audioEnabled: boolean;
  readonly musicEnabled: boolean;
  readonly hapticsEnabled: boolean;
  readonly colorAssistEnabled: boolean;
  readonly language: GameLanguage;
  readonly levelOrder: number;
  readonly hasAdService: boolean;
  readonly copy: ResolvedGameLanguage | UiCopy;
}): readonly SettingsRow[] {
  const copy = resolveSettingsCopy(options.copy);
  return [
    {
      id: "sound",
      icon: "sound",
      label: copy.soundFx,
      value: options.audioEnabled ? copy.on : copy.off,
      enabled: true,
      accent: options.audioEnabled
    },
    {
      id: "music",
      icon: "music",
      label: copy.music,
      value: options.musicEnabled ? copy.on : copy.off,
      enabled: true,
      accent: options.musicEnabled
    },
    {
      id: "language",
      icon: "language",
      label: copy.language,
      value: formatLanguageLabel(options.language),
      enabled: true
    },
    {
      id: "haptics",
      icon: "haptics",
      label: copy.haptics,
      value: options.hapticsEnabled ? copy.on : copy.off,
      enabled: true,
      accent: options.hapticsEnabled
    },
    {
      id: "palette",
      icon: "palette",
      label: copy.colorAssist,
      value: options.colorAssistEnabled ? copy.on : copy.off,
      enabled: true,
      accent: options.colorAssistEnabled
    },
    {
      id: "privacy",
      icon: "privacy",
      label: copy.privacyOptions,
      value: "",
      enabled: options.hasAdService
    },
    {
      id: "policy",
      icon: "policy",
      label: copy.privacyPolicy,
      value: "",
      enabled: true
    },
    {
      id: "previous",
      icon: "previous",
      label: copy.previousLevel,
      value: "",
      enabled: options.levelOrder > 1
    },
    {
      id: "about",
      icon: "about",
      label: copy.about,
      value: "",
      enabled: true
    }
  ];
}

export function createHapticsPreferenceAdapter(
  haptics: HapticFeedback,
  options: { readonly storage?: PreferenceStorage | null } = {}
): HapticsPreferenceAdapter {
  const storage = resolvePreferenceStorage(options.storage);
  const isEnabled = () => readHapticsPreference(storage);
  return {
    isEnabled,
    setEnabled(enabled: boolean) {
      writeHapticsPreference(enabled, storage);
    },
    toggleEnabled() {
      const next = !isEnabled();
      writeHapticsPreference(next, storage);
      return next;
    },
    selectionChanged() {
      if (isEnabled()) {
        return haptics.selectionChanged();
      }
      return undefined;
    },
    warning() {
      if (isEnabled()) {
        return haptics.warning();
      }
      return undefined;
    },
    impact() {
      if (isEnabled()) {
        return haptics.impact();
      }
      return undefined;
    }
  };
}

export function readHapticsPreference(
  storage: PreferenceStorage | null | undefined = getDefaultPreferenceStorage(),
  fallback = true
): boolean {
  try {
    const stored = storage?.getItem(GRIDFLOW_HAPTICS_STORAGE_KEY);
    if (stored === "true") {
      return true;
    }
    if (stored === "false") {
      return false;
    }
    return fallback;
  } catch {
    return fallback;
  }
}

export function writeHapticsPreference(
  enabled: boolean,
  storage: PreferenceStorage | null | undefined = getDefaultPreferenceStorage()
): void {
  try {
    storage?.setItem(GRIDFLOW_HAPTICS_STORAGE_KEY, enabled ? "true" : "false");
  } catch {
    return;
  }
}

export async function handlePrivacyOptionsAction(
  adService: Pick<AdService, "showPrivacyOptionsForm"> | null | undefined,
  onTracked?: (enabled: boolean) => void
): Promise<ConsentStatus | null> {
  if (!adService) {
    return null;
  }
  const status = await adService.showPrivacyOptionsForm();
  onTracked?.(true);
  return status;
}

export function openPrivacyPolicy(openUrl?: (url: string) => unknown): boolean {
  const opener = openUrl ?? getGlobalOpen();
  if (!opener) {
    return false;
  }
  try {
    opener(GRIDFLOW_PRIVACY_POLICY_URL);
    return true;
  } catch {
    return false;
  }
}

export function isFirstWinCelebration(
  levelOrder: number,
  highestUnlockedOrderBeforeComplete: number
): boolean {
  return levelOrder === 1 && highestUnlockedOrderBeforeComplete <= 1;
}

function cloneGhostGuideSpec(spec: GhostGuideSpec): GhostGuideSpec {
  return {
    kind: spec.kind,
    color: spec.color,
    path: cloneGuidePath(spec.path),
    ...(spec.preview
      ? {
          preview: {
            color: spec.preview.color,
            path: cloneGuidePath(spec.preview.path)
          }
        }
      : {}),
    ...(spec.eraseAnchor ? { eraseAnchor: { ...spec.eraseAnchor } } : {})
  };
}

function cloneGuidePath(path: readonly GridPoint[]): readonly GridPoint[] {
  return path.map((point) => ({ ...point }));
}

function isGuidePointAvailable(point: GridPoint, state: LevelState): boolean {
  if (point.x < 0 || point.x >= state.width || point.y < 0 || point.y >= state.height) {
    return false;
  }
  return !getCell(state, point).isObstacle;
}

export async function createGridFlowGame(
  host: HTMLElement,
  options: GridFlowGameOptions = {}
): Promise<GridFlowGame> {
  const catalog = options.catalog ?? developmentLevelCatalog;
  const progressService = options.progressService ?? createBrowserProgressService(catalog);
  const app = new Application();

  await app.init({
    antialias: true,
    autoDensity: true,
    background: options.background ?? GRIDFLOW_TOKENS.palette.background,
    preserveDrawingBuffer: options.preserveDrawingBuffer ?? false,
    resizeTo: host,
    resolution: options.resolution ?? window.devicePixelRatio
  });

  app.canvas.dataset.gridflowStage = "m5-playable-shell";
  if (getComputedStyle(host).position === "static") {
    host.style.position = "relative";
  }
  host.style.isolation = "isolate";
  app.canvas.style.position = "absolute";
  app.canvas.style.inset = "0";
  app.canvas.style.zIndex = "1";
  app.canvas.style.touchAction = "none";
  host.replaceChildren(app.canvas);

  return new PixiGridFlowGame(app, host, catalog, progressService, options);
}

class PixiGridFlowGame implements GridFlowGame {
  private readonly backgroundLayer = new Graphics();
  private readonly boardLayer = new Graphics();
  private readonly pathLayer = new Graphics();
  private readonly nodeLayer = new Container();
  private readonly uiLayer = new Container();
  private readonly completeLayer = new Graphics();
  private readonly celebrationLayer = new Graphics();
  private readonly buttonHitboxes: ButtonHitbox[] = [];
  private readonly undoHistory: LevelUndoSnapshot[] = [];
  private readonly celebrationParticles: CelebrationParticle[] = [];
  private readonly crossingEraseFeedbacks: CrossingEraseFeedbackEvent[] = [];
  private readonly guideOverlayElement = createGuideOverlayElement();
  private readonly resizeObserver: ResizeObserver | null;
  private readonly reducedMotion: boolean;
  private readonly colorVisionProfile: ColorVisionProfile;
  private readonly haptics: HapticsPreferenceAdapter;
  private readonly adService: AdService;
  private readonly hintService: HintService;
  private readonly audioService: AudioService;
  private readonly analyticsService: AnalyticsService;
  private readonly stuckDetectorOptions: StuckDetectorOptions;
  private readonly interstitialGate: InterstitialFrequencyGate;
  private readonly unsubscribeAdStatus: () => void;
  private readonly bottomSafeInset: number;

  private level: LevelCatalogEntry;
  private state: LevelState;
  private input: GridInputController;
  private stuckDetector: StuckDetector;
  private metrics: BoardMetrics;
  private activePointerId: number | null = null;
  private clearCandidate: LineClearCandidate | null = null;
  private guideActive = false;
  private guideProgress = 0;
  private completePulseSeconds = 0;
  private levelTransitionElapsedMs: number = RENDER_CONSTANTS.levelTransitionDurationMs;
  private completedLevelId: string | null = null;
  private firstWinCelebrationActive = false;
  private useColorBlindPalette: boolean;
  private currentTimeMs = 0;
  private hintInFlight = false;
  private rewardedHintConfirmationOpen = false;
  private lastHintColor: string | null = null;
  private lastRewardedHintResult: RewardedAdResult | null = null;
  private adStatus: AdStatusEvent | null = null;
  private lastPrivacyOptionsStatus: ConsentStatus | null = null;
  private lastPrivacyPolicyUrl: string | null = null;
  private levelSessionStartedAtMs = 0;
  private firstDrawTracked = false;
  private firstStuckTracked = false;
  private undoCount = 0;
  private clearCount = 0;
  private retryCount = 0;
  private levelAbandonTracked = false;
  private settingsOpen = false;
  private settingsPanel: SettingsPanel = "main";
  private language: GameLanguage = readLanguagePreference();

  public constructor(
    public readonly app: Application,
    private readonly host: HTMLElement,
    private readonly catalog: LevelCatalog,
    private readonly progressService: ProgressService,
    options: GridFlowGameOptions
  ) {
    this.reducedMotion = options.reducedMotion ?? readReducedMotionPreference();
    this.colorVisionProfile = options.colorVisionProfile ?? "standard";
    this.useColorBlindPalette = options.useColorBlindPalette ?? false;
    this.haptics = createHapticsPreferenceAdapter(options.haptics ?? createWebVibrationHaptics());
    this.adService = options.adService ?? createMockAdService();
    this.hintService = options.hintService ?? createSolverHintService();
    this.audioService = options.audioService ?? createBrowserAudioService();
    this.analyticsService = options.analyticsService ?? createMockAnalyticsService();
    this.stuckDetectorOptions = createStuckOptions(options);
    this.interstitialGate = new InterstitialFrequencyGate(options.interstitialGateOptions);
    this.bottomSafeInset = Math.max(
      GRIDFLOW_TOKENS.layout.safeBottom,
      Math.round(options.bottomSafeInset ?? 0)
    );
    this.level = this.resolveInitialLevel(options.levelId);
    this.state = createLevelState(this.level.config);
    this.metrics = this.computeMetrics();
    this.input = this.createInputController();
    this.stuckDetector = this.createStuckDetector();
    this.resizeObserver = createResizeObserver(() => this.handleResize());
    this.unsubscribeAdStatus = this.adService.onStatusChange((event) => {
      this.adStatus = event;
      this.render();
    });

    this.app.stage.addChild(
      this.backgroundLayer,
      this.boardLayer,
      this.pathLayer,
      this.completeLayer,
      this.celebrationLayer,
      this.nodeLayer,
      this.uiLayer
    );
    this.host.appendChild(this.guideOverlayElement);
    this.bindPointerEvents();
    window.addEventListener("pagehide", this.handlePageHide);
    this.resizeObserver?.observe(this.host);
    this.app.ticker.add(this.tick);
    this.installQaHooks();
    this.startGhostGuideIfNeeded();
    this.beginLevelAnalytics("initial");
    this.render();
  }

  public get canvas(): HTMLCanvasElement {
    return this.app.canvas;
  }

  public get currentLevelId(): string {
    return this.level.id;
  }

  public getSnapshot(): LevelSnapshot {
    return createLevelSnapshot(this.state);
  }

  public loadLevel(levelId: string): void {
    const nextLevel = getLevelById(this.catalog, levelId);
    if (!nextLevel) {
      throw new Error(`Cannot load unknown GridFlow level "${levelId}".`);
    }
    this.setLevel(nextLevel, true, "load");
  }

  public resetLevel(): void {
    this.retryCount += 1;
    this.trackAnalytics("level_reset", {
      levelOrder: this.level.order,
      retryCount: this.retryCount,
      elapsedMs: this.elapsedLevelMs
    });
    this.setLevel(this.level, true, "reset");
  }

  public previousLevel(): void {
    const previous = getLevelByOrder(this.catalog, this.level.order - 1);
    if (previous) {
      this.setLevel(previous, true, "previous");
    }
  }

  public nextLevel(): void {
    const next =
      getLevelByOrder(this.catalog, this.level.order + 1) ??
      getLevelByOrder(this.catalog, RENDER_CONSTANTS.finalLevelLoopOrder);
    if (next) {
      this.setLevel(next, true, "next");
    }
  }

  public destroy(): void {
    window.removeEventListener("pagehide", this.handlePageHide);
    this.trackLevelAbandoned("destroy");
    this.app.ticker.remove(this.tick);
    this.resizeObserver?.disconnect();
    this.unsubscribeAdStatus();
    this.audioService.destroy?.();
    this.unbindPointerEvents();
    this.uninstallQaHooks();
    this.guideOverlayElement.remove();
    destroyLayerChildren(this.nodeLayer);
    destroyLayerChildren(this.uiLayer);
    this.app.destroy({ removeView: true }, { children: true });
    this.host.replaceChildren();
  }

  private resolveInitialLevel(levelId: string | undefined): LevelCatalogEntry {
    const requestedLevel = levelId ? getLevelById(this.catalog, levelId) : null;
    if (requestedLevel) {
      this.progressService.setCurrentLevel(requestedLevel.id);
      return requestedLevel;
    }

    const saved = this.progressService.load();
    return getLevelById(this.catalog, saved.currentLevelId) ?? this.requireFirstLevel();
  }

  private setLevel(
    level: LevelCatalogEntry,
    persist: boolean,
    source: "load" | "previous" | "next" | "reset"
  ): void {
    this.activePointerId = null;
    this.level = level;
    this.state = createLevelState(level.config);
    this.metrics = this.computeMetrics();
    this.input = this.createInputController();
    this.stuckDetector = this.createStuckDetector();
    this.undoHistory.length = 0;
    this.clearCandidate = null;
    this.hintInFlight = false;
    this.rewardedHintConfirmationOpen = false;
    this.lastHintColor = null;
    this.lastRewardedHintResult = null;
    this.settingsOpen = false;
    this.settingsPanel = "main";
    this.guideProgress = 0;
    this.crossingEraseFeedbacks.length = 0;
    this.levelTransitionElapsedMs =
      source === "reset" ? RENDER_CONSTANTS.levelTransitionDurationMs : 0;
    this.resetCompletionEffects();
    if (persist) {
      this.progressService.setCurrentLevel(level.id);
    }
    this.startGhostGuideIfNeeded();
    if (source !== "reset") {
      this.beginLevelAnalytics(source);
    }
    this.render();
  }

  private createInputController(): GridInputController {
    return new GridInputController(this.state, this.metrics.geometry, { haptics: this.haptics });
  }

  private createStuckDetector(): StuckDetector {
    return new StuckDetector(this.level.order, this.stuckDetectorOptions);
  }

  private bindPointerEvents(): void {
    this.canvas.addEventListener("pointerdown", this.handlePointerDown, { passive: false });
    this.canvas.addEventListener("pointermove", this.handlePointerMove, { passive: false });
    this.canvas.addEventListener("pointerup", this.handlePointerEnd, { passive: false });
    this.canvas.addEventListener("pointercancel", this.handlePointerEnd, { passive: false });
    this.canvas.addEventListener("pointerleave", this.handlePointerEnd, { passive: false });
  }

  private unbindPointerEvents(): void {
    this.canvas.removeEventListener("pointerdown", this.handlePointerDown);
    this.canvas.removeEventListener("pointermove", this.handlePointerMove);
    this.canvas.removeEventListener("pointerup", this.handlePointerEnd);
    this.canvas.removeEventListener("pointercancel", this.handlePointerEnd);
    this.canvas.removeEventListener("pointerleave", this.handlePointerEnd);
  }

  private readonly handlePointerDown = (event: PointerEvent): void => {
    event.preventDefault();
    if (this.isLevelTransitionActive()) {
      return;
    }
    const point = this.eventToScreenPoint(event);
    const button = this.findButton(point);
    if (button) {
      this.handleButton(button.id);
      return;
    }
    if (this.rewardedHintConfirmationOpen) {
      this.rewardedHintConfirmationOpen = false;
      this.render();
      return;
    }
    if (this.settingsOpen) {
      this.settingsOpen = false;
      this.settingsPanel = "main";
      this.render();
      return;
    }

    this.stopGhostGuide();
    const undoSnapshot = this.captureUndoSnapshot();
    const clearCandidate = this.findLineClearCandidate(point);
    const result = this.input.touchStart(point);
    this.activePointerId = result.accepted ? event.pointerId : null;
    if (result.accepted) {
      this.pushUndoSnapshot(undoSnapshot);
      this.clearCandidate = clearCandidate;
      if (!this.getSnapshot().hasWon) {
        this.resetCompletionEffects();
      }
      capturePointer(this.canvas, event.pointerId);
    } else {
      this.clearCandidate = null;
    }
    this.render();
  };

  private readonly handlePointerMove = (event: PointerEvent): void => {
    if (this.isLevelTransitionActive()) {
      return;
    }
    if (this.activePointerId !== event.pointerId) {
      return;
    }
    event.preventDefault();
    const point = this.eventToScreenPoint(event);
    const previousSnapshot = this.getSnapshot();
    if (
      this.clearCandidate &&
      distanceBetween(point, this.clearCandidate.startPoint) > this.clickClearMaxDistance
    ) {
      this.clearCandidate = null;
    }
    const previousCommandCount = this.input.commandsExecuted;
    const result = this.input.touchMove(point);
    if (result.commandsExecuted > previousCommandCount) {
      this.stuckDetector.recordEffectiveAction();
      this.trackFirstDraw(result.activeColor);
      this.captureCrossingEraseFeedback(previousSnapshot, this.getSnapshot(), result.activeColor);
      this.playStepAudio(
        this.getSnapshot(),
        result.activeColor,
        result.commandsExecuted - previousCommandCount
      );
    }
    if (result.hasWon) {
      this.onLevelComplete();
    }
    this.playCompletionAudio(previousSnapshot, this.getSnapshot());
    this.render();
  };

  private readonly handlePointerEnd = (event: PointerEvent): void => {
    if (this.isLevelTransitionActive()) {
      return;
    }
    if (this.activePointerId !== event.pointerId) {
      return;
    }
    event.preventDefault();
    const result = this.input.touchEnd();
    const clearCandidate = this.clearCandidate;
    releasePointer(this.canvas, event.pointerId);
    this.activePointerId = null;
    this.clearCandidate = null;
    if (clearCandidate) {
      new ClearPathCommand(this.state, clearCandidate.color).execute();
      this.clearCount += 1;
      this.trackAnalytics("line_cleared", {
        color: clearCandidate.color,
        clearCount: this.clearCount,
        elapsedMs: this.elapsedLevelMs
      });
      this.stuckDetector.recordClear();
      this.stuckDetector.recordEffectiveAction();
      this.resetCompletionEffects();
    }
    this.stuckDetector.recordLengthIssue(this.getSnapshot());
    if (!clearCandidate && result.hasWon) {
      this.onLevelComplete();
    }
    this.render();
  };

  private readonly tick = (ticker: Ticker): void => {
    if (this.updateMotion(ticker.deltaMS)) {
      this.render();
    }
  };

  private readonly handlePageHide = (): void => {
    this.trackLevelAbandoned("pagehide");
  };

  private updateMotion(deltaMS: number): boolean {
    let needsRender = false;
    this.currentTimeMs += deltaMS;
    const previousHintSnapshot = this.stuckDetector.getSnapshot();
    this.stuckDetector.advance(deltaMS);
    const nextHintSnapshot = this.stuckDetector.getSnapshot();
    if (nextHintSnapshot.isHintAvailable !== previousHintSnapshot.isHintAvailable) {
      needsRender = true;
      if (nextHintSnapshot.isHintAvailable && !this.firstStuckTracked) {
        this.firstStuckTracked = true;
        this.trackAnalytics("first_stuck", {
          levelOrder: this.level.order,
          elapsedMs: this.elapsedLevelMs
        });
      }
    }
    if (
      nextHintSnapshot.isHintEligibleLevel &&
      !nextHintSnapshot.isHintAvailable &&
      Math.floor(nextHintSnapshot.progressRatio * 100) !==
        Math.floor(previousHintSnapshot.progressRatio * 100)
    ) {
      needsRender = true;
    }

    if (this.guideActive && !this.reducedMotion) {
      this.guideProgress += deltaMS / (GRIDFLOW_TOKENS.motion.guideSeconds * 1000);
      if (this.guideProgress >= 1 + RENDER_CONSTANTS.guideHoldRatio) {
        this.stopGhostGuide();
      }
      needsRender = true;
    }

    const snapshot = this.getSnapshot();
    if (
      snapshot.hasWon &&
      !this.reducedMotion &&
      isCompletionPulseActive(this.completePulseSeconds)
    ) {
      this.completePulseSeconds = Math.min(
        this.completePulseSeconds + deltaMS / 1000,
        getCompletionPulseDurationSeconds()
      );
      needsRender = true;
    }

    if (this.updateCelebrationParticles(deltaMS)) {
      needsRender = true;
    }

    if (this.updateCrossingEraseFeedbacks(deltaMS)) {
      needsRender = true;
    }

    if (
      !this.reducedMotion &&
      this.levelTransitionElapsedMs < RENDER_CONSTANTS.levelTransitionDurationMs
    ) {
      this.levelTransitionElapsedMs = Math.min(
        RENDER_CONSTANTS.levelTransitionDurationMs,
        this.levelTransitionElapsedMs + deltaMS
      );
      needsRender = true;
    }

    return needsRender;
  }

  private updateCrossingEraseFeedbacks(deltaMS: number): boolean {
    if (this.reducedMotion || this.crossingEraseFeedbacks.length === 0) {
      if (this.crossingEraseFeedbacks.length > 0) {
        this.crossingEraseFeedbacks.length = 0;
        return true;
      }
      return false;
    }

    for (let index = this.crossingEraseFeedbacks.length - 1; index >= 0; index -= 1) {
      const feedback = this.crossingEraseFeedbacks[index];
      if (!feedback) {
        continue;
      }
      feedback.ageMs += deltaMS;
      if (feedback.ageMs >= feedback.ttlMs) {
        this.crossingEraseFeedbacks.splice(index, 1);
      }
    }
    return true;
  }

  private isLevelTransitionActive(): boolean {
    return getLevelTransitionSnapshot(this.levelTransitionElapsedMs, this.reducedMotion).active;
  }

  private updateCelebrationParticles(deltaMS: number): boolean {
    if (this.reducedMotion || this.celebrationParticles.length === 0) {
      return false;
    }

    const deltaSeconds = deltaMS / 1000;
    const gravity =
      this.metrics.geometry.cellSize * GRIDFLOW_TOKENS.motion.celebrationParticleGravityRatio;
    const drag = Math.pow(GRIDFLOW_TOKENS.motion.celebrationParticleDrag, deltaMS / (1000 / 60));

    for (let index = this.celebrationParticles.length - 1; index >= 0; index -= 1) {
      const particle = this.celebrationParticles[index];
      if (!particle) {
        continue;
      }
      particle.ageMs += deltaMS;
      if (particle.ageMs >= particle.ttlMs) {
        this.celebrationParticles.splice(index, 1);
        continue;
      }
      particle.velocityY += gravity * deltaSeconds;
      particle.x += particle.velocityX * deltaSeconds;
      particle.y += particle.velocityY * deltaSeconds;
      particle.velocityX *= drag;
      particle.velocityY *= drag;
    }

    return true;
  }

  private handleResize(): void {
    this.metrics = this.computeMetrics();
    this.input = this.createInputController();
    this.activePointerId = null;
    this.clearCandidate = null;
    this.render();
  }

  private handleButton(id: ButtonId): void {
    if (id === "hintCancel") {
      this.rewardedHintConfirmationOpen = false;
      this.render();
      return;
    }
    if (id === "hintConfirm") {
      void this.handleConfirmedHintRequest();
      return;
    }
    if (id === "settings") {
      this.rewardedHintConfirmationOpen = false;
      this.settingsOpen = !this.settingsOpen;
      this.settingsPanel = "main";
      this.render();
      return;
    }
    if (id === "music") {
      const enabled = this.audioService.toggleMusicEnabled();
      this.trackAnalytics("settings_changed", { setting: "music", enabled });
      this.render();
      return;
    }
    if (id === "haptics") {
      const enabled = this.haptics.toggleEnabled();
      this.trackAnalytics("settings_changed", { setting: "haptics", enabled });
      this.render();
      return;
    }
    if (id === "language") {
      this.language = nextLanguage(this.language);
      writeLanguagePreference(this.language);
      this.trackAnalytics("settings_changed", { setting: "language", value: this.language });
      this.render();
      return;
    }
    if (id === "about") {
      this.settingsPanel = this.settingsPanel === "about" ? "main" : "about";
      this.render();
      return;
    }
    if (id === "reset") {
      this.rewardedHintConfirmationOpen = false;
      this.resetLevel();
      return;
    }
    if (id === "previous") {
      this.settingsOpen = false;
      this.settingsPanel = "main";
      this.previousLevel();
      return;
    }
    if (id === "undo") {
      if (this.undoLastAction()) {
        this.undoCount += 1;
        this.trackAnalytics("undo_used", {
          undoCount: this.undoCount,
          elapsedMs: this.elapsedLevelMs
        });
        this.stuckDetector.recordEffectiveAction();
      }
      return;
    }
    if (id === "hint") {
      void this.handleHintButton();
      return;
    }
    if (id === "sound") {
      const enabled = this.audioService.toggleEnabled();
      this.trackAnalytics("settings_changed", { setting: "sound", enabled });
      this.render();
      return;
    }
    if (id === "palette") {
      this.useColorBlindPalette = !this.useColorBlindPalette;
      this.trackAnalytics("settings_changed", {
        setting: "color_blind_palette",
        enabled: this.useColorBlindPalette
      });
      this.render();
      return;
    }
    if (id === "privacy") {
      void this.handlePrivacyOptions();
      return;
    }
    if (id === "policy") {
      this.lastPrivacyPolicyUrl = GRIDFLOW_PRIVACY_POLICY_URL;
      const opened = openPrivacyPolicy();
      this.trackAnalytics("settings_changed", { setting: "privacy_policy", enabled: opened });
      this.render();
      return;
    }
    if (id === "next" && this.getSnapshot().hasWon) {
      void this.advanceFromCompletedLevel();
    }
  }

  private async handlePrivacyOptions(): Promise<void> {
    try {
      this.lastPrivacyOptionsStatus = await handlePrivacyOptionsAction(
        this.adService,
        (enabled) => {
          this.trackAnalytics("settings_changed", { setting: "privacy_options", enabled });
        }
      );
    } catch {
      this.trackAnalytics("settings_changed", { setting: "privacy_options", enabled: false });
    } finally {
      this.render();
    }
  }

  private async handleHintButton(): Promise<void> {
    const snapshot = this.getSnapshot();
    const hintButtonState = this.getCurrentHintButtonState(snapshot);
    if (
      !shouldOpenRewardedHintConfirmation({
        confirmationOpen: this.rewardedHintConfirmationOpen,
        hintButtonState
      })
    ) {
      return;
    }

    this.rewardedHintConfirmationOpen = true;
    this.render();
  }

  private async handleConfirmedHintRequest(): Promise<void> {
    const snapshot = this.getSnapshot();
    if (this.hintInFlight || !this.rewardedHintConfirmationOpen || !this.canShowHint(snapshot)) {
      this.rewardedHintConfirmationOpen = false;
      this.render();
      return;
    }

    this.rewardedHintConfirmationOpen = false;
    this.hintInFlight = true;
    this.trackAnalytics("hint_requested", {
      elapsedMs: this.elapsedLevelMs,
      levelOrder: this.level.order
    });
    this.trackAnalytics("rewarded_ad_requested", {
      adType: "rewarded",
      adUnitAlias: "rewarded_hint_mock",
      placement: "hint"
    });
    this.render();
    try {
      const result = await this.adService.showRewardedHint();
      this.trackAnalytics("rewarded_ad_closed", {
        adType: "rewarded",
        placement: "hint",
        completed: result.completed,
        rewardGranted: result.rewardGranted,
        ...(result.errorCode ? { errorCode: result.errorCode } : {})
      });
      this.lastRewardedHintResult = result;
      if (!result.rewardGranted) {
        return;
      }

      const hint = this.hintService.getHint(this.level, this.getSnapshot());
      if (!hint) {
        return;
      }

      this.pushUndoSnapshot(this.captureUndoSnapshot());
      const previousSnapshot = this.getSnapshot();
      this.applyHint(hint);
      this.lastHintColor = hint.color;
      this.trackAnalytics("rewarded_ad_rewarded", {
        rewardType: "solver_path",
        hintColor: hint.color
      });
      this.stuckDetector.consumeHint();
      this.haptics.impact();
      if (this.getSnapshot().hasWon) {
        this.onLevelComplete();
      }
      this.playCompletionAudio(previousSnapshot, this.getSnapshot());
    } finally {
      this.hintInFlight = false;
      this.render();
    }
  }

  private async advanceFromCompletedLevel(): Promise<void> {
    const completedOrder = this.level.order;
    if (
      this.interstitialGate.canShowAfterLevel(completedOrder, this.currentTimeMs) &&
      this.adService.canShowInterstitial()
    ) {
      this.trackAnalytics("interstitial_ad_requested", {
        adType: "interstitial",
        adUnitAlias: "level_transition_mock",
        placement: "level_transition",
        completedLevelOrder: completedOrder
      });
      const result = await this.adService.showInterstitial();
      this.trackAnalytics("interstitial_ad_closed", {
        adType: "interstitial",
        placement: "level_transition",
        completed: result.completed,
        rewardGranted: false,
        ...(result.errorCode ? { errorCode: result.errorCode } : {})
      });
      if (result.completed) {
        this.interstitialGate.markShown(completedOrder, this.currentTimeMs);
      }
    }
    this.nextLevel();
  }

  private undoLastAction(): boolean {
    const snapshot = this.undoHistory.pop();
    if (!snapshot || snapshot.levelId !== this.level.id) {
      return false;
    }
    this.restoreUndoSnapshot(snapshot);
    this.input = this.createInputController();
    this.activePointerId = null;
    this.clearCandidate = null;
    this.resetCompletionEffects();
    this.render();
    return true;
  }

  private applyHint(hint: HintResult): void {
    const snapshot = this.getSnapshot();
    const nextState = createLevelState(this.level.config);
    restorePathCells(nextState, hint.color, hint.cells);

    const hintedCells = new Set(hint.cells.map(pointKey));
    for (const path of snapshot.paths) {
      if (path.color === hint.color || path.cells.some((cell) => hintedCells.has(pointKey(cell)))) {
        continue;
      }
      restorePathCells(nextState, path.color, path.cells);
    }

    this.state = nextState;
    this.input = this.createInputController();
    this.activePointerId = null;
    this.clearCandidate = null;
    this.resetCompletionEffects();
  }

  private canShowHint(snapshot: LevelSnapshot): boolean {
    return this.getCurrentHintButtonState(snapshot).enabled;
  }

  private getCurrentHintButtonState(snapshot: LevelSnapshot): HintButtonState {
    return getHintButtonState({
      hasWon: snapshot.hasWon,
      inFlight: this.hintInFlight,
      canShowRewardedHint: this.adService.canShowRewardedHint(),
      detector: this.stuckDetector.getSnapshot()
    });
  }

  private playStepAudio(
    snapshot: LevelSnapshot,
    activeColor: string | null,
    commandDelta: number
  ): void {
    if (!activeColor || commandDelta <= 0) {
      return;
    }
    const path = snapshot.paths.find((candidate) => candidate.color === activeColor);
    if (!path) {
      return;
    }
    const firstStepIndex = Math.max(0, path.currentLength - 1 - commandDelta);
    for (let offset = 0; offset < commandDelta; offset += 1) {
      this.audioService.playStep(firstStepIndex + offset);
    }
  }

  private playCompletionAudio(previousSnapshot: LevelSnapshot, nextSnapshot: LevelSnapshot): void {
    if (!previousSnapshot.hasWon && nextSnapshot.hasWon) {
      if (this.firstWinCelebrationActive) {
        this.audioService.playFirstWin();
      } else {
        this.audioService.playLevelComplete();
      }
      return;
    }
    const previousCompleteColors = new Set(
      previousSnapshot.paths.filter((path) => path.isComplete).map((path) => path.color)
    );
    if (
      nextSnapshot.paths.some((path) => path.isComplete && !previousCompleteColors.has(path.color))
    ) {
      this.audioService.playPathComplete();
    }
  }

  private captureCrossingEraseFeedback(
    previousSnapshot: LevelSnapshot,
    nextSnapshot: LevelSnapshot,
    activeColor: string | null
  ): void {
    const feedback = createCrossingEraseFeedback(
      previousSnapshot.paths,
      nextSnapshot.paths,
      activeColor
    );
    if (!feedback || this.reducedMotion) {
      return;
    }
    this.crossingEraseFeedbacks.push({
      ...feedback,
      ageMs: 0,
      ttlMs: RENDER_CONSTANTS.crossingEraseFeedbackTtlMs
    });
    this.audioService.playCrossingErase();
  }

  private onLevelComplete(): void {
    const snapshot = this.getSnapshot();
    if (!snapshot.hasWon) {
      return;
    }
    if (this.completedLevelId === this.level.id) {
      return;
    }
    const progressBeforeComplete = this.progressService.load();
    this.firstWinCelebrationActive = isFirstWinCelebration(
      this.level.order,
      progressBeforeComplete.highestUnlockedOrder
    );
    this.completedLevelId = this.level.id;
    this.progressService.markLevelComplete(this.level.id);
    this.trackAnalytics("level_completed", {
      levelOrder: this.level.order,
      durationMs: this.elapsedLevelMs,
      undoCount: this.undoCount,
      clearCount: this.clearCount,
      retryCount: this.retryCount
    });
    this.completePulseSeconds = 0;
    this.celebrationParticles.length = 0;
    if (!this.reducedMotion) {
      this.spawnCompletionParticles(snapshot, this.firstWinCelebrationActive ? 1.5 : 1);
    }
  }

  private resetCompletionEffects(): void {
    this.completedLevelId = null;
    this.firstWinCelebrationActive = false;
    this.completePulseSeconds = 0;
    this.celebrationParticles.length = 0;
  }

  private beginLevelAnalytics(source: "initial" | "load" | "previous" | "next"): void {
    this.levelSessionStartedAtMs = this.currentTimeMs;
    this.firstDrawTracked = false;
    this.firstStuckTracked = false;
    this.undoCount = 0;
    this.clearCount = 0;
    this.retryCount = 0;
    this.levelAbandonTracked = false;
    this.trackAnalytics("level_entered", {
      levelOrder: this.level.order,
      source
    });
  }

  private trackLevelAbandoned(source: LevelAbandonSource): void {
    const elapsedMs = this.elapsedLevelMs;
    const hasWon = this.completedLevelId === this.level.id || this.getSnapshot().hasWon;
    if (
      !shouldTrackLevelAbandoned({
        alreadyTracked: this.levelAbandonTracked,
        elapsedMs,
        hasWon
      })
    ) {
      return;
    }

    this.levelAbandonTracked = true;
    this.trackAnalytics("level_abandoned", {
      levelOrder: this.level.order,
      elapsedMs,
      undoCount: this.undoCount,
      clearCount: this.clearCount,
      retryCount: this.retryCount,
      source
    });
  }

  private trackFirstDraw(activeColor: string | null): void {
    if (this.firstDrawTracked) {
      return;
    }
    this.firstDrawTracked = true;
    this.trackAnalytics("first_draw", {
      elapsedMs: this.elapsedLevelMs,
      ...(activeColor ? { color: activeColor } : {})
    });
  }

  private trackAnalytics(name: AnalyticsEventName, fields: AnalyticsFields = {}): void {
    this.analyticsService.track({
      name,
      levelId: this.level.id,
      timestampMs: Math.round(this.currentTimeMs),
      fields
    });
  }

  private get elapsedLevelMs(): number {
    return Math.max(0, Math.round(this.currentTimeMs - this.levelSessionStartedAtMs));
  }

  private spawnCompletionParticles(snapshot: LevelSnapshot, intensity = 1): void {
    const particlesPerPath = Math.max(
      RENDER_CONSTANTS.celebrationMinCellsPerParticle,
      Math.floor(
        (RENDER_CONSTANTS.celebrationMaxParticles * intensity) / Math.max(1, snapshot.paths.length)
      )
    );
    for (const path of snapshot.paths) {
      if (!path.isComplete || path.cells.length === 0) {
        continue;
      }
      const token = this.resolvePathVisual(path.color);
      const interval = Math.max(
        1,
        Math.ceil(
          path.cells.length /
            Math.min(RENDER_CONSTANTS.celebrationParticleTargetPerPath, particlesPerPath)
        )
      );
      for (let index = 0; index < path.cells.length; index += interval) {
        const cell = path.cells[index];
        if (
          !cell ||
          this.celebrationParticles.length >= RENDER_CONSTANTS.celebrationMaxParticles * intensity
        ) {
          break;
        }
        this.celebrationParticles.push(
          this.createCelebrationParticle(cell, token.glowColor, path.color, index)
        );
      }
    }
  }

  private createCelebrationParticle(
    cell: GridPoint,
    color: number,
    pathColor: string,
    index: number
  ): CelebrationParticle {
    const center = gridCellCenter(cell, this.metrics.geometry);
    const seed = hashParticleSeed(pathColor, cell.x, cell.y, index);
    const angle = seed * Math.PI * 2;
    const speedRatio =
      GRIDFLOW_TOKENS.motion.celebrationParticleSpeedMinRatio +
      ((seed * RENDER_CONSTANTS.colorHashPrime) % 1) *
        (GRIDFLOW_TOKENS.motion.celebrationParticleSpeedMaxRatio -
          GRIDFLOW_TOKENS.motion.celebrationParticleSpeedMinRatio);
    const speed = speedRatio * this.metrics.geometry.cellSize;
    const offset = (seed - 0.5) * this.metrics.geometry.cellSize * 0.18;
    return {
      x: center.x + Math.cos(angle) * offset,
      y: center.y + Math.sin(angle) * offset,
      velocityX: Math.cos(angle) * speed,
      velocityY: Math.sin(angle) * speed,
      ageMs: 0,
      ttlMs: GRIDFLOW_TOKENS.motion.celebrationParticleTtlMs,
      radius:
        this.metrics.geometry.cellSize * GRIDFLOW_TOKENS.motion.celebrationParticleRadiusRatio,
      color
    };
  }

  private startGhostGuideIfNeeded(): void {
    const guidePath = createGhostGuidePathForLevel(this.level.order, this.state);
    this.guideActive =
      guidePath.length > 0 && !this.progressService.hasSeenGhostGuide(this.level.id);
    this.guideProgress = this.reducedMotion ? 1 : 0;
  }

  private stopGhostGuide(): void {
    if (!this.guideActive) {
      return;
    }
    this.guideActive = false;
    this.progressService.markGhostGuideSeen(this.level.id);
  }

  private eventToScreenPoint(event: PointerEvent): ScreenPoint {
    const rect = this.canvas.getBoundingClientRect();
    const scaleX =
      this.app.renderer.screen.width /
      Math.max(rect.width, RENDER_CONSTANTS.pointerCoordinateFallbackSize);
    const scaleY =
      this.app.renderer.screen.height /
      Math.max(rect.height, RENDER_CONSTANTS.pointerCoordinateFallbackSize);
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY
    };
  }

  private findButton(point: ScreenPoint): ButtonHitbox | null {
    return (
      this.buttonHitboxes.find(
        (button) =>
          button.enabled &&
          point.x >= button.x &&
          point.x <= button.x + button.width &&
          point.y >= button.y &&
          point.y <= button.y + button.height
      ) ?? null
    );
  }

  private findLineClearCandidate(point: ScreenPoint): LineClearCandidate | null {
    const gridPoint = screenToGrid(point, this.metrics.geometry);
    if (!gridPoint) {
      return null;
    }

    const cell = getCell(this.state, gridPoint);
    if (cell.hasNode || cell.occupiedByColor === null) {
      return null;
    }

    const path = this.state.paths.get(cell.occupiedByColor);
    if (!path || path.cells.length <= 1) {
      return null;
    }

    return {
      color: cell.occupiedByColor,
      startPoint: point
    };
  }

  private get clickClearMaxDistance(): number {
    return this.metrics.geometry.cellSize * RENDER_CONSTANTS.clickClearMaxDistanceRatio;
  }

  private captureUndoSnapshot(): LevelUndoSnapshot {
    return {
      levelId: this.level.id,
      paths: Array.from(this.state.paths.values()).map((path) => ({
        color: path.color,
        cells: path.cells.map((cell) => ({ ...cell }))
      }))
    };
  }

  private pushUndoSnapshot(snapshot: LevelUndoSnapshot): void {
    this.undoHistory.push(snapshot);
    while (this.undoHistory.length > RENDER_CONSTANTS.undoHistoryLimit) {
      this.undoHistory.shift();
    }
  }

  private restoreUndoSnapshot(snapshot: LevelUndoSnapshot): void {
    const restoredState = createLevelState(this.level.config);
    for (const path of snapshot.paths) {
      restorePathCells(restoredState, path.color, path.cells);
    }
    this.state = restoredState;
  }

  private computeMetrics(): BoardMetrics {
    const screen = this.app.renderer.screen;
    const screenWidth = Math.max(screen.width, GRIDFLOW_TOKENS.layout.minViewportWidth);
    const screenHeight = screen.height;
    const margin =
      screenWidth <= 420
        ? GRIDFLOW_TOKENS.layout.compactOuterMargin
        : GRIDFLOW_TOKENS.layout.outerMargin;
    const availableWidth = Math.max(
      GRIDFLOW_TOKENS.layout.minCellSize,
      screenWidth - margin * 2 - GRIDFLOW_TOKENS.layout.boardPadding * 2
    );
    const availableHeight = Math.max(
      GRIDFLOW_TOKENS.layout.minCellSize,
      screenHeight -
        GRIDFLOW_TOKENS.layout.headerHeight -
        GRIDFLOW_TOKENS.layout.footerHeight -
        this.bottomSafeInset -
        margin -
        GRIDFLOW_TOKENS.layout.boardPadding * 2
    );
    const rawCellSize = Math.floor(
      Math.min(availableWidth / this.level.config.width, availableHeight / this.level.config.height)
    );
    const cellSize = clampCellSize(rawCellSize);
    const boardWidth = cellSize * this.level.config.width;
    const boardHeight = cellSize * this.level.config.height;
    const centeredBoardY =
      GRIDFLOW_TOKENS.layout.headerHeight + Math.max(0, (availableHeight - boardHeight) / 2);
    const stageLift = Math.min(20, screenHeight * 0.024);
    const geometry = {
      offsetX: (screenWidth - boardWidth) / 2,
      offsetY: Math.max(GRIDFLOW_TOKENS.layout.headerHeight, centeredBoardY - stageLift),
      cellSize,
      width: this.level.config.width,
      height: this.level.config.height
    };

    return {
      geometry,
      boardWidth,
      boardHeight,
      bottomSafeInset: this.bottomSafeInset,
      controlDock: this.computeControlDockMetrics(screenWidth, screenHeight, margin),
      screenWidth,
      screenHeight
    };
  }

  private computeControlDockMetrics(
    screenWidth: number,
    screenHeight: number,
    margin: number
  ): ControlDockMetrics {
    const width = Math.min(RENDER_CONSTANTS.controlDockWidth, screenWidth - margin * 2);
    const height = RENDER_CONSTANTS.controlDockHeight;
    const x = (screenWidth - width) / 2;
    const y = screenHeight - this.bottomSafeInset - height;
    return {
      x,
      y,
      width,
      height,
      centerX: x + width * 0.5,
      centerY: y + height / 2
    };
  }

  private render(): void {
    const snapshot = this.getSnapshot();
    this.drawBackground();
    this.drawBoard(snapshot);
    this.drawPaths(snapshot);
    this.drawCrossingEraseFeedbacks();
    this.drawCompleteOverlay(snapshot);
    this.drawCelebrationEffects(snapshot);
    this.drawNodes(snapshot);
    this.drawUi(snapshot);
    this.drawGuide();
    this.drawLevelTransitionOverlay();
  }

  private drawBackground(): void {
    this.backgroundLayer
      .clear()
      .rect(0, 0, this.metrics.screenWidth, this.metrics.screenHeight)
      .fill({ color: GRIDFLOW_TOKENS.palette.background });

    this.backgroundLayer
      .circle(
        this.metrics.screenWidth * 0.5,
        this.metrics.screenHeight * 0.4,
        this.metrics.screenWidth * 0.72
      )
      .fill({ color: 0x0b2a3b, alpha: 0.08 });
    this.backgroundLayer
      .circle(
        this.metrics.screenWidth * 0.48,
        this.metrics.screenHeight * 0.73,
        this.metrics.screenWidth * 0.5
      )
      .fill({ color: 0x231438, alpha: 0.06 });
  }

  private drawBoard(snapshot: LevelSnapshot): void {
    const { geometry, boardWidth, boardHeight } = this.metrics;
    const padding = Math.max(2, geometry.cellSize * 0.02);
    const panelX = geometry.offsetX;
    const panelY = geometry.offsetY;
    this.boardLayer.clear();

    const stagePadX = Math.max(10, geometry.cellSize * 0.08);
    const stagePadY =
      geometry.height === 1
        ? Math.max(20, geometry.cellSize * 0.34)
        : Math.max(10, geometry.cellSize * 0.1);
    const stageRadius = Math.max(14, GRIDFLOW_TOKENS.board.cornerRadius + geometry.cellSize * 0.08);
    this.boardLayer
      .roundRect(
        panelX - stagePadX,
        panelY - stagePadY,
        boardWidth + stagePadX * 2,
        boardHeight + stagePadY * 2,
        stageRadius
      )
      .fill({ color: 0x091523, alpha: 0.48 })
      .stroke({
        width: Math.max(1, geometry.cellSize * 0.01),
        color: this.resolvePathVisual("blue").glowColor,
        alpha: 0.16
      });

    this.boardLayer
      .roundRect(
        panelX - geometry.cellSize * 0.04,
        panelY - geometry.cellSize * 0.04,
        boardWidth + geometry.cellSize * 0.08,
        boardHeight + geometry.cellSize * 0.08,
        GRIDFLOW_TOKENS.board.cornerRadius + 7
      )
      .fill({
        color: this.resolvePathVisual("blue").glowColor,
        alpha: RENDER_CONSTANTS.boardGlowAlpha
      });
    this.boardLayer
      .roundRect(panelX, panelY, boardWidth, boardHeight, GRIDFLOW_TOKENS.board.cornerRadius)
      .fill({
        color: GRIDFLOW_TOKENS.palette.boardPanel,
        alpha: RENDER_CONSTANTS.boardPanelAlpha
      })
      .stroke({
        width: Math.max(1, geometry.cellSize * 0.012),
        color: GRIDFLOW_TOKENS.palette.gridLineStrong,
        alpha: 0.72
      });

    for (let column = 0; column <= geometry.width; column += 1) {
      const x = panelX + column * geometry.cellSize;
      this.boardLayer.moveTo(x, panelY + padding);
      this.boardLayer.lineTo(x, panelY + boardHeight - padding);
    }
    for (let row = 0; row <= geometry.height; row += 1) {
      const y = panelY + row * geometry.cellSize;
      this.boardLayer.moveTo(panelX + padding, y);
      this.boardLayer.lineTo(panelX + boardWidth - padding, y);
    }
    this.boardLayer.stroke({
      width: Math.max(1, geometry.cellSize * 0.009),
      color: GRIDFLOW_TOKENS.palette.gridLine,
      alpha: RENDER_CONSTANTS.cellStrokeAlpha
    });

    for (const row of snapshot.grid) {
      for (const cell of row) {
        if (cell.isObstacle) {
          this.boardLayer
            .roundRect(
              geometry.offsetX + cell.x * geometry.cellSize + padding,
              geometry.offsetY + cell.y * geometry.cellSize + padding,
              geometry.cellSize - padding * 2,
              geometry.cellSize - padding * 2,
              GRIDFLOW_TOKENS.board.cornerRadius
            )
            .fill({ color: GRIDFLOW_TOKENS.palette.obstacle, alpha: 0.9 });
        }
      }
    }

    const dotRadius = Math.max(1.5, geometry.cellSize * 0.026);
    for (let column = 0; column <= geometry.width; column += 1) {
      for (let row = 0; row <= geometry.height; row += 1) {
        this.boardLayer
          .circle(panelX + column * geometry.cellSize, panelY + row * geometry.cellSize, dotRadius)
          .fill({
            color: GRIDFLOW_TOKENS.palette.gridLineStrong,
            alpha: RENDER_CONSTANTS.gridDotAlpha
          });
      }
    }
  }

  private drawGuide(): void {
    this.guideOverlayElement.replaceChildren();
    if (!this.guideActive || this.settingsOpen || this.rewardedHintConfirmationOpen) {
      return;
    }

    const guide = this.createGhostGuideSpec();
    if (!guide || guide.path.length === 0) {
      return;
    }

    const svg = createSvgElement("svg");
    setSvgAttributes(svg, {
      width: `${this.metrics.screenWidth}`,
      height: `${this.metrics.screenHeight}`,
      viewBox: `0 0 ${this.metrics.screenWidth} ${this.metrics.screenHeight}`,
      "aria-hidden": "true"
    });

    if (guide.preview) {
      const previewToken = this.resolvePathVisual(guide.preview.color);
      appendSvgPolyline(
        svg,
        guide.preview.path,
        this.metrics.geometry,
        numberToCssColor(previewToken.glowColor),
        RENDER_CONSTANTS.guideAlpha * 0.42,
        this.metrics.geometry.cellSize * GRIDFLOW_TOKENS.board.lineGlowWidthRatio
      );
      appendSvgPolyline(
        svg,
        guide.preview.path,
        this.metrics.geometry,
        numberToCssColor(previewToken.color),
        RENDER_CONSTANTS.guideAlpha * 0.74,
        this.metrics.geometry.cellSize * GRIDFLOW_TOKENS.board.guideLineWidthRatio * 1.2
      );
      appendSvgWaypoints(
        svg,
        guide.preview.path,
        this.metrics.geometry,
        numberToCssColor(previewToken.color),
        RENDER_CONSTANTS.guideAlpha * 0.72
      );
    }

    const visibleCount = this.reducedMotion
      ? guide.path.length
      : Math.max(
          RENDER_CONSTANTS.guideMinVisibleCells,
          Math.ceil(guide.path.length * Math.min(1, this.guideProgress))
        );
    const visiblePath = guide.path.slice(0, visibleCount);
    const token = this.resolvePathVisual(guide.color);
    appendSvgPolyline(
      svg,
      visiblePath,
      this.metrics.geometry,
      numberToCssColor(token.glowColor),
      RENDER_CONSTANTS.guideAlpha * 0.48,
      this.metrics.geometry.cellSize * GRIDFLOW_TOKENS.board.lineGlowWidthRatio
    );
    appendSvgPolyline(
      svg,
      visiblePath,
      this.metrics.geometry,
      numberToCssColor(GRIDFLOW_TOKENS.palette.textPrimary),
      RENDER_CONSTANTS.guideAlpha * 0.86,
      this.metrics.geometry.cellSize * GRIDFLOW_TOKENS.board.guideLineWidthRatio * 1.25
    );
    appendSvgWaypoints(
      svg,
      visiblePath,
      this.metrics.geometry,
      numberToCssColor(GRIDFLOW_TOKENS.palette.textPrimary),
      RENDER_CONSTANTS.guideAlpha * 0.92
    );

    if (guide.kind === "two-end-start") {
      const start = visiblePath[0];
      if (start) {
        appendSvgCircle(
          svg,
          gridCellCenter(start, this.metrics.geometry),
          this.metrics.geometry.cellSize * 0.28,
          numberToCssColor(token.glowColor),
          RENDER_CONSTANTS.guideAlpha * 0.82,
          "none"
        );
      }
    }

    if (guide.kind === "crossing-erase" && guide.eraseAnchor) {
      const pulse = this.reducedMotion ? 0.62 : 0.45 + Math.sin(this.currentTimeMs / 145) * 0.18;
      appendSvgCircle(
        svg,
        gridCellCenter(guide.eraseAnchor, this.metrics.geometry),
        this.metrics.geometry.cellSize * 0.34,
        numberToCssColor(token.glowColor),
        RENDER_CONSTANTS.guideAlpha * pulse,
        "none"
      );
    }
    this.guideOverlayElement.appendChild(svg);
  }

  private drawPaths(snapshot: LevelSnapshot): void {
    this.pathLayer.clear();
    for (const path of snapshot.paths) {
      if (path.cells.length <= 1) {
        continue;
      }
      const token = this.resolvePathVisual(path.color);
      const isComplete = path.isComplete;
      const alpha = isComplete ? RENDER_CONSTANTS.lineCompleteAlpha : token.dimAlpha;
      if (!this.reducedMotion) {
        this.drawPolyline(this.pathLayer, path.cells, {
          color: token.glowColor,
          alpha: isComplete
            ? RENDER_CONSTANTS.lineGlowAlpha
            : RENDER_CONSTANTS.lineGlowAlpha * 0.46,
          width: this.metrics.geometry.cellSize * GRIDFLOW_TOKENS.board.lineGlowWidthRatio
        });
      }
      this.drawPolyline(this.pathLayer, path.cells, {
        color: token.color,
        alpha: isComplete ? alpha : RENDER_CONSTANTS.lineInactiveAlpha,
        width: this.metrics.geometry.cellSize * GRIDFLOW_TOKENS.board.lineWidthRatio
      });
      if (path.currentLength > path.targetLength) {
        this.drawOverlengthTailWarning(path);
      }
    }

    const activePath = this.findActivePath(snapshot);
    if (activePath) {
      const tail = activePath.cells[activePath.cells.length - 1];
      if (tail) {
        const center = gridCellCenter(tail, this.metrics.geometry);
        const token = this.resolvePathVisual(activePath.color);
        if (!this.reducedMotion) {
          this.pathLayer
            .circle(
              center.x,
              center.y,
              this.metrics.geometry.cellSize * GRIDFLOW_TOKENS.board.activeHeadRadiusRatio * 1.9
            )
            .fill({ color: token.glowColor, alpha: RENDER_CONSTANTS.lineGlowAlpha });
        }
        this.pathLayer
          .circle(
            center.x,
            center.y,
            this.metrics.geometry.cellSize * GRIDFLOW_TOKENS.board.activeHeadRadiusRatio
          )
          .fill({ color: token.glowColor, alpha: RENDER_CONSTANTS.activeHeadAlpha });
      }
    }
  }

  private drawOverlengthTailWarning(path: PathSnapshot): void {
    const tail = path.cells[path.cells.length - 1];
    if (!tail) {
      return;
    }
    const center = gridCellCenter(tail, this.metrics.geometry);
    const pulse = this.reducedMotion ? 0.5 : 0.5 + Math.sin(this.currentTimeMs / 120) * 0.5;
    this.pathLayer
      .roundRect(
        center.x - this.metrics.geometry.cellSize * 0.42,
        center.y - this.metrics.geometry.cellSize * 0.42,
        this.metrics.geometry.cellSize * 0.84,
        this.metrics.geometry.cellSize * 0.84,
        GRIDFLOW_TOKENS.board.cornerRadius
      )
      .stroke({
        width: Math.max(2, this.metrics.geometry.cellSize * 0.035),
        color: GRIDFLOW_TOKENS.palette.danger,
        alpha: 0.48 + pulse * 0.34
      });
  }

  private drawCrossingEraseFeedbacks(): void {
    if (this.reducedMotion || this.crossingEraseFeedbacks.length === 0) {
      return;
    }

    for (const feedback of this.crossingEraseFeedbacks) {
      const progress = clamp01(feedback.ageMs / feedback.ttlMs);
      const alpha = easeOutCubic(1 - progress);
      const erasedToken = this.resolvePathVisual(feedback.erasedColor);
      const activeToken = this.resolvePathVisual(feedback.activeColor);
      const anchorCenter = gridCellCenter(feedback.anchor, this.metrics.geometry);

      this.drawPolyline(this.pathLayer, feedback.erasedCells, {
        color: erasedToken.glowColor,
        alpha: alpha * 0.6,
        width: this.metrics.geometry.cellSize * GRIDFLOW_TOKENS.board.lineGlowWidthRatio * 1.18
      });
      this.drawPolyline(this.pathLayer, feedback.erasedCells, {
        color: erasedToken.color,
        alpha: alpha * 0.72,
        width: this.metrics.geometry.cellSize * GRIDFLOW_TOKENS.board.lineWidthRatio * 1.08
      });

      this.pathLayer
        .circle(
          anchorCenter.x,
          anchorCenter.y,
          this.metrics.geometry.cellSize * (0.28 + progress * 0.28)
        )
        .stroke({
          width: Math.max(2, this.metrics.geometry.cellSize * 0.035),
          color: activeToken.glowColor,
          alpha: alpha * 0.9
        });
      this.pathLayer
        .circle(anchorCenter.x, anchorCenter.y, this.metrics.geometry.cellSize * 0.16)
        .fill({ color: activeToken.glowColor, alpha: alpha * 0.42 });
    }
  }

  private drawLevelTransitionOverlay(): void {
    const transition = getLevelTransitionSnapshot(
      this.levelTransitionElapsedMs,
      this.reducedMotion
    );
    const lift =
      (1 - transition.easedProgress) * Math.min(18, this.metrics.geometry.cellSize * 0.16);
    this.boardLayer.y = -lift;
    this.pathLayer.y = -lift;
    this.nodeLayer.y = -lift;
    this.completeLayer.y = -lift;
    this.celebrationLayer.y = -lift;
    if (!transition.active) {
      return;
    }
    const alpha = (1 - transition.easedProgress) * 0.34;
    const shade = new Graphics()
      .rect(0, 0, this.metrics.screenWidth, this.metrics.screenHeight)
      .fill({ color: GRIDFLOW_TOKENS.palette.background, alpha });
    this.uiLayer.addChild(shade);
  }

  private drawCompleteOverlay(snapshot: LevelSnapshot): void {
    this.completeLayer.clear();
    if (!snapshot.hasWon) {
      return;
    }

    const pulseAlpha =
      this.reducedMotion || !isCompletionPulseActive(this.completePulseSeconds)
        ? GRIDFLOW_TOKENS.motion.reducedMotionPulseAlpha
        : Math.sin(
            (this.completePulseSeconds / GRIDFLOW_TOKENS.motion.completePulseSeconds) * Math.PI * 2
          ) *
            GRIDFLOW_TOKENS.motion.completePulseAlpha +
          GRIDFLOW_TOKENS.motion.completePulseAlpha;

    this.completeLayer
      .roundRect(
        this.metrics.geometry.offsetX - GRIDFLOW_TOKENS.layout.boardPadding,
        this.metrics.geometry.offsetY - GRIDFLOW_TOKENS.layout.boardPadding,
        this.metrics.boardWidth + GRIDFLOW_TOKENS.layout.boardPadding * 2,
        this.metrics.boardHeight + GRIDFLOW_TOKENS.layout.boardPadding * 2,
        GRIDFLOW_TOKENS.board.cornerRadius
      )
      .fill({
        color: GRIDFLOW_TOKENS.palette.success,
        alpha: Math.max(RENDER_CONSTANTS.completeOverlayAlpha, pulseAlpha)
      });
  }

  private drawCelebrationEffects(snapshot: LevelSnapshot): void {
    this.celebrationLayer.clear();
    if (!snapshot.hasWon || this.reducedMotion) {
      return;
    }

    const flashProgress = Math.min(
      1,
      this.completePulseSeconds / GRIDFLOW_TOKENS.motion.celebrationFlashSeconds
    );
    const flashAlpha =
      easeOutCubic(1 - flashProgress) *
      RENDER_CONSTANTS.celebrationFlashAlpha *
      (this.firstWinCelebrationActive ? 1.35 : 1);
    if (flashAlpha > 0.01) {
      for (const path of snapshot.paths) {
        if (!path.isComplete || path.cells.length <= 1) {
          continue;
        }
        const token = this.resolvePathVisual(path.color);
        this.drawPolyline(this.celebrationLayer, path.cells, {
          color: token.glowColor,
          alpha: flashAlpha,
          width:
            this.metrics.geometry.cellSize * GRIDFLOW_TOKENS.board.celebrationLineGlowWidthRatio
        });
      }
    }

    for (const particle of this.celebrationParticles) {
      const progress = Math.min(1, particle.ageMs / particle.ttlMs);
      const alpha = easeOutCubic(1 - progress);
      this.celebrationLayer
        .circle(particle.x, particle.y, particle.radius * (1 + progress * 0.65))
        .fill({ color: particle.color, alpha })
        .stroke({
          width: Math.max(1, particle.radius * 0.38),
          color: GRIDFLOW_TOKENS.palette.textPrimary,
          alpha: alpha * 0.32
        });
    }
  }

  private drawNodes(snapshot: LevelSnapshot): void {
    destroyLayerChildren(this.nodeLayer);
    for (const row of snapshot.grid) {
      for (const cell of row) {
        if (!cell.hasNode || cell.nodeColor === null || cell.nodeRole === null) {
          continue;
        }
        const nodeColor = cell.nodeColor;
        const nodeRole = cell.nodeRole;
        const path = snapshot.paths.find((candidate) => candidate.color === nodeColor);
        if (!path) {
          continue;
        }
        this.nodeLayer.addChild(
          this.createNode({ x: cell.x, y: cell.y, nodeColor, nodeRole }, path)
        );
      }
    }
  }

  private createNode(
    cell: {
      readonly x: number;
      readonly y: number;
      readonly nodeRole: "origin" | "target";
      readonly nodeColor: string;
    },
    path: PathSnapshot
  ): Container {
    const group = new Container();
    const token = this.resolvePathVisual(cell.nodeColor);
    const center = gridCellCenter(cell, this.metrics.geometry);
    const nodeRadius = Math.min(
      this.metrics.geometry.cellSize * GRIDFLOW_TOKENS.board.nodeRadiusRatio,
      this.metrics.screenWidth * 0.075
    );
    const markerRadius = Math.min(
      this.metrics.geometry.cellSize * GRIDFLOW_TOKENS.board.shapeRadiusRatio,
      nodeRadius * 0.42
    );

    const glow = new Graphics()
      .circle(center.x, center.y, nodeRadius * 1.48)
      .fill({ color: token.color, alpha: RENDER_CONSTANTS.nodeGlowAlpha });
    const node = new Graphics()
      .circle(center.x, center.y, nodeRadius)
      .fill({ color: GRIDFLOW_TOKENS.palette.background, alpha: RENDER_CONSTANTS.nodeFillAlpha })
      .stroke({
        width: Math.max(3, this.metrics.geometry.cellSize * 0.052),
        color: token.color,
        alpha: path.isComplete ? 1 : RENDER_CONSTANTS.enabledButtonAlpha
      });
    node
      .circle(center.x, center.y, nodeRadius * 0.62)
      .fill({ color: token.color, alpha: 0.16 })
      .stroke({
        width: Math.max(1, this.metrics.geometry.cellSize * 0.015),
        color: token.glowColor,
        alpha: 0.45
      });
    drawNodeShape(node, token.shape, center.x, center.y, markerRadius * 1.14, token);
    group.addChild(glow, node);

    const label = cell.nodeRole === "origin" ? formatOriginCounter(path) : token.label;
    if (cell.nodeRole !== "origin" && !this.useColorBlindPalette) {
      return group;
    }

    const labelColor =
      cell.nodeRole === "target"
        ? GRIDFLOW_TOKENS.palette.textSecondary
        : resolveNodeLabelColor(path, cell.nodeRole);
    const labelText = new Text({
      text: label,
      anchor: 0.5,
      style: {
        align: "center",
        fill: labelColor,
        fontFamily: GRIDFLOW_TOKENS.typography.family,
        fontSize:
          this.metrics.geometry.cellSize *
          (label.length > 2
            ? GRIDFLOW_TOKENS.typography.smallNodeSizeRatio
            : GRIDFLOW_TOKENS.typography.nodeSizeRatio),
        fontWeight: "700"
      }
    });
    const boardRight = this.metrics.geometry.offsetX + this.metrics.boardWidth;
    const preferRight = center.x + nodeRadius * 2.2 < boardRight;
    const labelDirection = preferRight ? 1 : -1;
    const labelNeighborX = cell.x + labelDirection;
    const neighborHasNode =
      labelNeighborX >= 0 &&
      labelNeighborX < this.state.width &&
      getCell(this.state, { x: labelNeighborX, y: cell.y }).hasNode;
    const labelInsideNode = path.isComplete || cell.nodeRole === "target" || neighborHasNode;
    const externalLabelX =
      center.x + labelDirection * (nodeRadius + this.metrics.geometry.cellSize * 0.27);
    labelText.position.set(
      labelInsideNode ? center.x : externalLabelX,
      labelInsideNode ? center.y : center.y
    );
    group.addChild(labelText);
    return group;
  }

  private drawUi(snapshot: LevelSnapshot): void {
    destroyLayerChildren(this.uiLayer);
    this.buttonHitboxes.length = 0;

    const margin =
      this.metrics.screenWidth <= 420
        ? GRIDFLOW_TOKENS.layout.compactOuterMargin
        : GRIDFLOW_TOKENS.layout.outerMargin;
    const headerCenterY = Math.max(42, GRIDFLOW_TOKENS.layout.headerHeight * 0.38);
    const actionY = headerCenterY - RENDER_CONSTANTS.topActionSize / 2;

    this.drawIconButton(
      "settings",
      "settings",
      margin,
      actionY,
      RENDER_CONSTANTS.previousButtonSize,
      true
    );
    this.drawLevelBadge(this.metrics.screenWidth / 2, headerCenterY);
    this.drawPathProgress(snapshot.paths, this.metrics.screenWidth / 2, headerCenterY + 38);

    const resetX = this.metrics.screenWidth - margin - RENDER_CONSTANTS.topActionSize;
    const undoX = resetX - 10 - RENDER_CONSTANTS.topActionSize;
    this.drawIconButton(
      "undo",
      "undo",
      undoX,
      actionY,
      RENDER_CONSTANTS.topActionSize,
      this.undoHistory.length > 0
    );
    this.drawIconButton("reset", "reset", resetX, actionY, RENDER_CONSTANTS.topActionSize, true);

    this.drawControlDock(snapshot);
    if (this.settingsOpen) {
      this.buttonHitboxes.length = 0;
      this.drawSettingsOverlay(margin);
      return;
    }
    if (this.rewardedHintConfirmationOpen) {
      this.buttonHitboxes.length = 0;
      this.drawRewardedHintConfirmationOverlay(margin);
    }
  }

  private drawRewardedHintConfirmationOverlay(margin: number): void {
    const copy = resolveUiCopy(this.language);
    const panelWidth = Math.min(334, this.metrics.screenWidth - margin * 2);
    const panelHeight = 218;
    const panelX = (this.metrics.screenWidth - panelWidth) / 2;
    const panelY = Math.max(104, (this.metrics.screenHeight - panelHeight) / 2);
    const accentColor = this.resolvePathVisual("blue").glowColor;

    const shade = new Graphics()
      .rect(0, 0, this.metrics.screenWidth, this.metrics.screenHeight)
      .fill({ color: 0x000000, alpha: 0.54 });
    this.uiLayer.addChild(shade);

    const panel = new Graphics()
      .roundRect(panelX, panelY, panelWidth, panelHeight, 22)
      .fill({ color: GRIDFLOW_TOKENS.palette.boardPanel, alpha: 0.97 })
      .stroke({
        width: 1.2,
        color: GRIDFLOW_TOKENS.palette.gridLineStrong,
        alpha: 0.46
      });
    panel.roundRect(panelX + 7, panelY + 7, panelWidth - 14, panelHeight - 14, 18).stroke({
      width: 1,
      color: GRIDFLOW_TOKENS.palette.textPrimary,
      alpha: 0.08
    });
    panel
      .circle(panelX + 36, panelY + 38, 18)
      .fill({ color: accentColor, alpha: 0.16 })
      .circle(panelX + 36, panelY + 38, 13)
      .stroke({ width: 1.4, color: accentColor, alpha: 0.72 });
    this.drawIconGlyph(
      panel,
      "hint",
      panelX + 36,
      panelY + 38,
      26,
      GRIDFLOW_TOKENS.palette.textPrimary,
      0.92
    );
    this.uiLayer.addChild(panel);

    const title = new Text({
      text: copy.hintConfirmTitle,
      anchor: { x: 0, y: 0.5 },
      style: {
        fill: GRIDFLOW_TOKENS.palette.textPrimary,
        fontFamily: GRIDFLOW_TOKENS.typography.family,
        fontSize: 20,
        fontWeight: "800"
      }
    });
    title.position.set(panelX + 64, panelY + 38);
    this.uiLayer.addChild(title);

    const body = new Text({
      text: copy.hintConfirmBody,
      anchor: { x: 0, y: 0 },
      style: {
        fill: GRIDFLOW_TOKENS.palette.textSecondary,
        fontFamily: GRIDFLOW_TOKENS.typography.family,
        fontSize: 14,
        fontWeight: "600",
        lineHeight: 22,
        wordWrap: true,
        wordWrapWidth: panelWidth - 44
      }
    });
    body.position.set(panelX + 22, panelY + 76);
    this.uiLayer.addChild(body);

    const buttonGap = 10;
    const buttonHeight = 46;
    const buttonY = panelY + panelHeight - buttonHeight - 18;
    const buttonWidth = (panelWidth - 44 - buttonGap) / 2;
    this.drawDialogActionButton({
      id: "hintCancel",
      icon: "close",
      label: copy.cancel,
      x: panelX + 22,
      y: buttonY,
      width: buttonWidth,
      height: buttonHeight,
      accent: false
    });
    this.drawDialogActionButton({
      id: "hintConfirm",
      icon: "next",
      label: copy.watchAd,
      x: panelX + 22 + buttonWidth + buttonGap,
      y: buttonY,
      width: buttonWidth,
      height: buttonHeight,
      accent: true
    });
  }

  private drawControlDock(snapshot: LevelSnapshot): void {
    const {
      x: dockX,
      y: dockY,
      width: dockWidth,
      height: dockHeight,
      centerX: primaryCenterX,
      centerY: dockCenterY
    } = this.metrics.controlDock;

    const dock = new Graphics()
      .roundRect(dockX, dockY, dockWidth, dockHeight, dockHeight / 2)
      .fill({ color: GRIDFLOW_TOKENS.palette.boardPanel, alpha: 0.72 })
      .stroke({
        width: 1.2,
        color: GRIDFLOW_TOKENS.palette.gridLineStrong,
        alpha: 0.34
      });
    dock.roundRect(dockX + 6, dockY + 6, dockWidth - 12, dockHeight - 12, dockHeight / 2).stroke({
      width: 1,
      color: GRIDFLOW_TOKENS.palette.textPrimary,
      alpha: 0.06
    });
    this.uiLayer.addChild(dock);

    if (snapshot.hasWon) {
      this.drawPrimaryActionButton("next", "next", primaryCenterX, dockCenterY, true, true);
      this.registerButton("hint", false, primaryCenterX, dockCenterY, 0, 0);
    } else {
      const hintButtonState = this.getCurrentHintButtonState(snapshot);
      this.drawPrimaryActionButton(
        "hint",
        "hint",
        primaryCenterX,
        dockCenterY,
        hintButtonState.enabled,
        hintButtonState.kind === "ready",
        hintButtonState
      );
      this.registerButton("next", false, primaryCenterX, dockCenterY, 0, 0);
    }
  }

  private drawSettingsOverlay(margin: number): void {
    const copy = resolveUiCopy(this.language);
    const settingsRows = getSettingsRows({
      audioEnabled: this.audioService.isEnabled(),
      musicEnabled: this.audioService.isMusicEnabled(),
      hapticsEnabled: this.haptics.isEnabled(),
      colorAssistEnabled: this.useColorBlindPalette,
      language: this.language,
      levelOrder: this.level.order,
      hasAdService: Boolean(this.adService),
      copy
    });
    const panelWidth = Math.min(342, this.metrics.screenWidth - margin * 2);
    const closeSize = 38;
    const rowMetrics = resolveSettingsRowMetrics(
      settingsRows.length,
      this.metrics.screenHeight,
      margin
    );
    const panelHeight =
      this.settingsPanel === "about"
        ? 316
        : 70 +
          settingsRows.length * rowMetrics.height +
          Math.max(0, settingsRows.length - 1) * rowMetrics.gap +
          18;
    const panelX = (this.metrics.screenWidth - panelWidth) / 2;
    const panelY = Math.max(margin, (this.metrics.screenHeight - panelHeight) / 2);
    const rowX = panelX + 18;
    const rowWidth = panelWidth - 36;

    const shade = new Graphics()
      .rect(0, 0, this.metrics.screenWidth, this.metrics.screenHeight)
      .fill({ color: 0x000000, alpha: 0.5 });
    this.uiLayer.addChild(shade);

    const panel = new Graphics()
      .roundRect(panelX, panelY, panelWidth, panelHeight, 22)
      .fill({ color: GRIDFLOW_TOKENS.palette.boardPanel, alpha: 0.96 })
      .stroke({
        width: 1.2,
        color: GRIDFLOW_TOKENS.palette.gridLineStrong,
        alpha: 0.42
      });
    panel.roundRect(panelX + 7, panelY + 7, panelWidth - 14, panelHeight - 14, 18).stroke({
      width: 1,
      color: GRIDFLOW_TOKENS.palette.textPrimary,
      alpha: 0.06
    });
    this.uiLayer.addChild(panel);

    const title = new Text({
      text: this.settingsPanel === "about" ? copy.aboutTitle : copy.settings,
      anchor: { x: 0, y: 0.5 },
      style: {
        fill: GRIDFLOW_TOKENS.palette.textPrimary,
        fontFamily: GRIDFLOW_TOKENS.typography.family,
        fontSize: 20,
        fontWeight: "800"
      }
    });
    title.position.set(panelX + 20, panelY + 35);
    this.uiLayer.addChild(title);
    this.drawIconButton(
      "settings",
      "close",
      panelX + panelWidth - closeSize - 16,
      panelY + 16,
      closeSize,
      true
    );

    if (this.settingsPanel === "about") {
      this.drawAboutPanel(copy, rowX, panelY + 78, rowWidth);
      this.drawSettingsRow({
        id: "about",
        icon: "previous",
        label: copy.back,
        value: "",
        x: rowX,
        y: panelY + panelHeight - rowMetrics.height - 18,
        width: rowWidth,
        height: rowMetrics.height,
        enabled: true
      });
      return;
    }

    let rowY = panelY + 70;
    for (const row of settingsRows) {
      this.drawSettingsRow({
        ...row,
        x: rowX,
        y: rowY,
        width: rowWidth,
        height: rowMetrics.height
      });
      rowY += rowMetrics.height + rowMetrics.gap;
    }
  }

  private drawAboutPanel(copy: UiCopy, x: number, y: number, width: number): void {
    const text = new Text({
      text: copy.aboutBody.join("\n"),
      anchor: { x: 0, y: 0 },
      style: {
        fill: GRIDFLOW_TOKENS.palette.textSecondary,
        fontFamily: GRIDFLOW_TOKENS.typography.family,
        fontSize: 14,
        fontWeight: "600",
        lineHeight: 26,
        wordWrap: true,
        wordWrapWidth: width
      }
    });
    text.position.set(x + 4, y);
    this.uiLayer.addChild(text);
  }

  private drawSettingsRow(options: {
    readonly id: ButtonId;
    readonly icon: ButtonIcon;
    readonly label: string;
    readonly value: string;
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
    readonly enabled: boolean;
    readonly accent?: boolean;
  }): void {
    const alpha = options.enabled
      ? RENDER_CONSTANTS.enabledButtonAlpha
      : RENDER_CONSTANTS.disabledButtonAlpha;
    const accentColor = options.accent
      ? GRIDFLOW_TOKENS.palette.success
      : GRIDFLOW_TOKENS.palette.gridLineStrong;
    const row = new Container();
    const shape = new Graphics()
      .roundRect(options.x, options.y, options.width, options.height, 14)
      .fill({
        color: options.accent
          ? GRIDFLOW_TOKENS.palette.success
          : GRIDFLOW_TOKENS.palette.buttonFill,
        alpha: options.accent ? 0.12 : options.enabled ? 0.52 : 0.22
      })
      .stroke({
        width: 1,
        color: accentColor,
        alpha: options.accent ? 0.74 : options.enabled ? 0.28 : 0.16
      });
    this.drawIconGlyph(
      shape,
      options.icon,
      options.x + 25,
      options.y + options.height / 2,
      31,
      options.accent ? GRIDFLOW_TOKENS.palette.success : GRIDFLOW_TOKENS.palette.textSecondary,
      alpha
    );
    row.addChild(shape);

    const label = new Text({
      text: options.label,
      anchor: { x: 0, y: 0.5 },
      style: {
        fill: GRIDFLOW_TOKENS.palette.textPrimary,
        fontFamily: GRIDFLOW_TOKENS.typography.family,
        fontSize: 14,
        fontWeight: "700"
      }
    });
    label.alpha = alpha;
    label.position.set(options.x + 52, options.y + options.height / 2);
    row.addChild(label);

    if (options.value) {
      const value = new Text({
        text: options.value,
        anchor: { x: 1, y: 0.5 },
        style: {
          fill: options.accent
            ? GRIDFLOW_TOKENS.palette.success
            : GRIDFLOW_TOKENS.palette.textMuted,
          fontFamily: GRIDFLOW_TOKENS.typography.family,
          fontSize: 12,
          fontWeight: "800"
        }
      });
      value.alpha = alpha;
      value.position.set(options.x + options.width - 14, options.y + options.height / 2);
      row.addChild(value);
    }

    this.uiLayer.addChild(row);
    this.registerButton(
      options.id,
      options.enabled,
      options.x,
      options.y,
      options.width,
      options.height
    );
  }

  private drawLevelBadge(centerX: number, centerY: number): void {
    const copy = resolveUiCopy(this.language);
    const width = Math.min(126, this.metrics.screenWidth * 0.34);
    const height = RENDER_CONSTANTS.hudPillHeight;
    const x = centerX - width / 2;
    const y = centerY - height / 2;
    const shape = new Graphics()
      .roundRect(x, y, width, height, height / 2)
      .fill({ color: GRIDFLOW_TOKENS.palette.buttonFill, alpha: 0.68 })
      .stroke({
        width: 1,
        color: GRIDFLOW_TOKENS.palette.gridLineStrong,
        alpha: 0.34
      });
    this.uiLayer.addChild(shape);

    const eyebrow = new Text({
      text: copy.level,
      anchor: 0.5,
      style: {
        fill: GRIDFLOW_TOKENS.palette.textMuted,
        fontFamily: GRIDFLOW_TOKENS.typography.family,
        fontSize: 9,
        fontWeight: "700"
      }
    });
    eyebrow.position.set(centerX, centerY - 9);
    this.uiLayer.addChild(eyebrow);

    const label = new Text({
      text: `${formatLevelNumber(this.level.order)} / ${this.catalog.entries.length}`,
      anchor: 0.5,
      style: {
        fill: GRIDFLOW_TOKENS.palette.textPrimary,
        fontFamily: GRIDFLOW_TOKENS.typography.family,
        fontSize: 15,
        fontWeight: "800"
      }
    });
    label.position.set(centerX, centerY + 8);
    this.uiLayer.addChild(label);
  }

  private renderGameToText(): string {
    const snapshot = this.getSnapshot();
    const ghostGuideSpec = this.createGhostGuideSpec();
    const stuckSnapshot = this.stuckDetector.getSnapshot();
    const hintButtonState = this.getCurrentHintButtonState(snapshot);
    const settingsRows = getSettingsRows({
      audioEnabled: this.audioService.isEnabled(),
      musicEnabled: this.audioService.isMusicEnabled(),
      hapticsEnabled: this.haptics.isEnabled(),
      colorAssistEnabled: this.useColorBlindPalette,
      language: this.language,
      levelOrder: this.level.order,
      hasAdService: Boolean(this.adService),
      copy: resolveUiCopy(this.language)
    });
    return JSON.stringify({
      coordinateSystem: "grid origin top-left, x right, y down",
      level: {
        id: this.level.id,
        order: this.level.order,
        width: snapshot.width,
        height: snapshot.height,
        fillRequired: snapshot.fillRequired
      },
      layout: {
        geometry: this.metrics.geometry,
        boardWidth: this.metrics.boardWidth,
        boardHeight: this.metrics.boardHeight,
        bottomSafeInset: this.metrics.bottomSafeInset,
        controlDock: this.metrics.controlDock,
        screenWidth: this.metrics.screenWidth,
        screenHeight: this.metrics.screenHeight
      },
      state: {
        hasWon: snapshot.hasWon,
        occupiedCount: snapshot.occupiedCount,
        fillableCount: snapshot.fillableCount,
        activeColor: this.input.activeColor,
        undoDepth: this.undoHistory.length,
        colorBlindPalette: this.useColorBlindPalette,
        colorVisionProfile: this.colorVisionProfile,
        reducedMotion: this.reducedMotion,
        settings: {
          open: this.settingsOpen,
          panel: this.settingsPanel,
          language: this.language,
          effectiveLanguage: resolveEffectiveLanguage(this.language),
          hapticsEnabled: this.haptics.isEnabled(),
          rows: settingsRows.map((row) => ({
            id: row.id,
            label: row.label,
            value: row.value,
            enabled: row.enabled
          }))
        },
        privacy: {
          policyUrl: GRIDFLOW_PRIVACY_POLICY_URL,
          lastPolicyUrl: this.lastPrivacyPolicyUrl,
          lastOptionsStatus: this.lastPrivacyOptionsStatus
        },
        guide: {
          active: this.guideActive,
          progress: Number(Math.min(1, this.guideProgress).toFixed(3)),
          kind: ghostGuideSpec?.kind ?? null,
          color: ghostGuideSpec?.color ?? null,
          pathLength: ghostGuideSpec?.path.length ?? 0,
          previewPathLength: ghostGuideSpec?.preview?.path.length ?? 0,
          eraseAnchor: ghostGuideSpec?.eraseAnchor ?? null
        },
        celebrationParticleCount: this.celebrationParticles.length,
        firstWinCelebrationActive: this.firstWinCelebrationActive,
        completionPulseActive:
          snapshot.hasWon &&
          !this.reducedMotion &&
          isCompletionPulseActive(this.completePulseSeconds),
        completionPulseSeconds: Number(this.completePulseSeconds.toFixed(3)),
        completionFlashActive:
          snapshot.hasWon &&
          !this.reducedMotion &&
          this.completePulseSeconds < GRIDFLOW_TOKENS.motion.celebrationFlashSeconds,
        levelTransition: getLevelTransitionSnapshot(
          this.levelTransitionElapsedMs,
          this.reducedMotion
        ),
        crossingEraseFeedbacks: this.crossingEraseFeedbacks.map((feedback) => ({
          activeColor: feedback.activeColor,
          erasedColor: feedback.erasedColor,
          anchor: feedback.anchor,
          erasedCellCount: feedback.erasedCells.length,
          progress: Number(clamp01(feedback.ageMs / feedback.ttlMs).toFixed(3))
        })),
        hint: {
          ...stuckSnapshot,
          buttonState: hintButtonState.kind,
          buttonEnabled: hintButtonState.enabled,
          buttonProgressRatio: Number(hintButtonState.progressRatio.toFixed(3)),
          inFlight: this.hintInFlight,
          lastHintColor: this.lastHintColor,
          lastRewardGranted: this.lastRewardedHintResult?.rewardGranted ?? null,
          confirmationOpen: this.rewardedHintConfirmationOpen
        },
        ad: {
          canShowRewardedHint: this.adService.canShowRewardedHint(),
          canShowInterstitial: this.adService.canShowInterstitial(),
          lastStatus: this.adStatus
        },
        audio: this.audioService.getSnapshot(),
        analytics: {
          ...this.analyticsService.getSnapshot(),
          session: {
            elapsedMs: this.elapsedLevelMs,
            firstDrawTracked: this.firstDrawTracked,
            firstStuckTracked: this.firstStuckTracked,
            undoCount: this.undoCount,
            clearCount: this.clearCount,
            retryCount: this.retryCount
          }
        }
      },
      paths: snapshot.paths.map((path) => ({
        color: path.color,
        currentLength: path.currentLength,
        targetLength: path.targetLength,
        remainingLength: path.remainingLength,
        isLinked: path.isLinked,
        isComplete: path.isComplete,
        cells: path.cells
      })),
      buttons: this.buttonHitboxes.map((button) => ({
        id: button.id,
        enabled: button.enabled,
        x: Math.round(button.x),
        y: Math.round(button.y),
        width: Math.round(button.width),
        height: Math.round(button.height)
      }))
    });
  }

  private advanceTime(ms: number): void {
    if (!Number.isFinite(ms) || ms <= 0) {
      return;
    }
    const frameMs = 1000 / 60;
    const steps = Math.max(1, Math.ceil(ms / frameMs));
    for (let step = 0; step < steps; step += 1) {
      this.updateMotion(frameMs);
    }
    this.render();
  }

  private installQaHooks(): void {
    if (typeof window === "undefined") {
      return;
    }
    window.render_game_to_text = () => this.renderGameToText();
    window.advanceTime = (ms: number) => this.advanceTime(ms);
  }

  private uninstallQaHooks(): void {
    if (typeof window === "undefined") {
      return;
    }
    if (window.render_game_to_text) {
      Reflect.deleteProperty(window, "render_game_to_text");
    }
    if (window.advanceTime) {
      Reflect.deleteProperty(window, "advanceTime");
    }
  }

  private drawPathProgress(paths: readonly PathSnapshot[], centerX: number, y: number): void {
    const totalDots = Math.max(1, paths.length);
    const progressWidth = (totalDots - 1) * RENDER_CONSTANTS.progressDotGap;
    const firstDotX = centerX - progressWidth / 2;
    const progress = new Graphics();
    if (totalDots > 1) {
      const lastDotX = firstDotX + progressWidth;
      progress.moveTo(firstDotX, y);
      progress.lineTo(lastDotX, y);
      progress.stroke({
        width: 1,
        color: GRIDFLOW_TOKENS.palette.gridLineStrong,
        alpha: 0.3
      });
    }

    for (let index = 0; index < totalDots; index += 1) {
      const path = paths[index];
      const token = path ? this.resolvePathVisual(path.color) : this.resolvePathVisual("blue");
      const isComplete = path?.isComplete ?? false;
      progress
        .circle(
          firstDotX + index * RENDER_CONSTANTS.progressDotGap,
          y,
          RENDER_CONSTANTS.progressDotRadius
        )
        .fill({
          color: isComplete ? token.glowColor : GRIDFLOW_TOKENS.palette.background,
          alpha: isComplete ? 1 : 0.74
        })
        .stroke({
          width: 1.4,
          color: token.glowColor,
          alpha: isComplete ? 1 : 0.46
        });
    }

    this.uiLayer.addChild(progress);
  }

  private drawIconButton(
    id: ButtonId,
    icon: ButtonIcon,
    x: number,
    y: number,
    size: number,
    enabled: boolean
  ): void {
    const button = new Container();
    const alpha = enabled
      ? RENDER_CONSTANTS.enabledButtonAlpha
      : RENDER_CONSTANTS.disabledButtonAlpha;
    const shape = new Graphics()
      .roundRect(x, y, size, size, size * 0.42)
      .fill({ color: GRIDFLOW_TOKENS.palette.buttonFill, alpha: enabled ? 0.54 : 0.22 })
      .stroke({
        width: 1.2,
        color: GRIDFLOW_TOKENS.palette.buttonStroke,
        alpha: enabled ? 0.56 : 0.24
      });
    this.drawIconGlyph(
      shape,
      icon,
      x + size / 2,
      y + size / 2,
      size,
      GRIDFLOW_TOKENS.palette.textPrimary,
      alpha
    );
    button.addChild(shape);
    this.uiLayer.addChild(button);
    this.registerButton(id, enabled, x, y, size, size);
  }

  private drawDockIconButton(
    id: ButtonId,
    icon: ButtonIcon,
    centerX: number,
    centerY: number,
    size: number,
    enabled: boolean,
    accent = false
  ): void {
    const alpha = enabled
      ? RENDER_CONSTANTS.enabledButtonAlpha
      : RENDER_CONSTANTS.disabledButtonAlpha;
    const color = accent ? GRIDFLOW_TOKENS.palette.success : GRIDFLOW_TOKENS.palette.textSecondary;
    const button = new Container();
    const shape = new Graphics()
      .circle(centerX, centerY, size / 2)
      .fill({
        color: accent ? GRIDFLOW_TOKENS.palette.success : GRIDFLOW_TOKENS.palette.buttonFill,
        alpha: accent ? 0.18 : enabled ? 0.58 : 0.22
      })
      .stroke({
        width: 1,
        color,
        alpha: accent ? 0.82 : enabled ? 0.45 : 0.22
      });
    this.drawIconGlyph(shape, icon, centerX, centerY, size * 0.86, color, alpha);
    button.addChild(shape);
    this.uiLayer.addChild(button);
    this.registerButton(id, enabled, centerX - size / 2, centerY - size / 2, size, size);
  }

  private drawPrimaryActionButton(
    id: "hint" | "next",
    icon: "hint" | "next",
    centerX: number,
    centerY: number,
    enabled: boolean,
    accent: boolean,
    hintState?: HintButtonState
  ): void {
    const radius = RENDER_CONSTANTS.primaryActionRadius;
    const alpha = enabled
      ? RENDER_CONSTANTS.enabledButtonAlpha
      : RENDER_CONSTANTS.disabledButtonAlpha;
    const color = accent
      ? GRIDFLOW_TOKENS.palette.success
      : this.resolvePathVisual("blue").glowColor;
    const button = new Container();
    const shape = new Graphics()
      .circle(centerX, centerY, radius * 1.18)
      .fill({ color, alpha: enabled ? 0.16 : 0.03 })
      .circle(centerX, centerY, radius)
      .fill({ color: GRIDFLOW_TOKENS.palette.buttonFill, alpha: enabled ? 0.9 : 0.5 })
      .stroke({
        width: Math.max(1.2, radius * 0.035),
        color,
        alpha: enabled ? alpha : 0.28
      })
      .circle(centerX, centerY, radius * 0.72)
      .stroke({
        width: 1,
        color: GRIDFLOW_TOKENS.palette.textPrimary,
        alpha: alpha * 0.16
      });
    this.drawIconGlyph(
      shape,
      icon,
      centerX,
      centerY,
      radius * 1.32,
      GRIDFLOW_TOKENS.palette.textPrimary,
      alpha
    );
    if (id === "hint" && hintState?.kind === "charging" && hintState.progressRatio > 0) {
      shape
        .arc(
          centerX,
          centerY,
          radius * 1.04,
          -Math.PI / 2,
          -Math.PI / 2 + Math.PI * 2 * clamp01(hintState.progressRatio)
        )
        .stroke({
          width: Math.max(2, radius * 0.08),
          color: this.resolvePathVisual("blue").glowColor,
          alpha: 0.54,
          cap: "round"
        });
    }
    if (id === "hint" && hintState?.kind === "loading") {
      shape.circle(centerX, centerY, radius * 1.04).stroke({
        width: Math.max(2, radius * 0.07),
        color: this.resolvePathVisual("blue").glowColor,
        alpha: 0.42
      });
    }
    if (id === "hint" && enabled) {
      shape.circle(centerX + radius * 0.66, centerY - radius * 0.66, radius * 0.1).fill({
        color: 0xffd447,
        alpha: 1
      });
    }
    button.addChild(shape);

    if (enabled) {
      const copy = resolveUiCopy(this.language);
      const label = new Text({
        text: id === "hint" ? copy.hint : copy.next,
        anchor: 0.5,
        style: {
          fill: GRIDFLOW_TOKENS.palette.textPrimary,
          fontFamily: GRIDFLOW_TOKENS.typography.family,
          fontSize: GRIDFLOW_TOKENS.typography.labelSize,
          fontWeight: "700"
        }
      });
      label.position.set(centerX, centerY + RENDER_CONSTANTS.primaryActionLabelGap);
      button.addChild(label);
    }
    this.uiLayer.addChild(button);
    this.registerButton(id, enabled, centerX - radius, centerY - radius, radius * 2, radius * 2);
  }

  private drawDialogActionButton(options: {
    readonly id: "hintCancel" | "hintConfirm";
    readonly icon: "close" | "next";
    readonly label: string;
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
    readonly accent: boolean;
  }): void {
    const color = options.accent
      ? this.resolvePathVisual("blue").glowColor
      : GRIDFLOW_TOKENS.palette.gridLineStrong;
    const button = new Container();
    const shape = new Graphics()
      .roundRect(options.x, options.y, options.width, options.height, 14)
      .fill({
        color: options.accent ? color : GRIDFLOW_TOKENS.palette.buttonFill,
        alpha: options.accent ? 0.22 : 0.54
      })
      .stroke({
        width: 1,
        color,
        alpha: options.accent ? 0.82 : 0.36
      });
    this.drawIconGlyph(
      shape,
      options.icon,
      options.x + 23,
      options.y + options.height / 2,
      27,
      GRIDFLOW_TOKENS.palette.textPrimary,
      0.94
    );
    button.addChild(shape);

    const label = new Text({
      text: options.label,
      anchor: { x: 0, y: 0.5 },
      style: {
        fill: GRIDFLOW_TOKENS.palette.textPrimary,
        fontFamily: GRIDFLOW_TOKENS.typography.family,
        fontSize: 13,
        fontWeight: "800"
      }
    });
    label.position.set(options.x + 44, options.y + options.height / 2);
    button.addChild(label);

    this.uiLayer.addChild(button);
    this.registerButton(options.id, true, options.x, options.y, options.width, options.height);
  }

  private registerButton(
    id: ButtonId,
    enabled: boolean,
    x: number,
    y: number,
    width: number,
    height: number
  ): void {
    this.buttonHitboxes.push({ id, enabled, x, y, width, height });
  }

  private drawIconGlyph(
    graphics: Graphics,
    icon: ButtonIcon,
    centerX: number,
    centerY: number,
    size: number,
    color: number,
    alpha: number
  ): void {
    const strokeWidth = Math.max(2, size * 0.07);
    const radius = size * 0.24;
    if (icon === "settings") {
      graphics.circle(centerX, centerY, radius * 0.72).stroke({ width: strokeWidth, color, alpha });
      for (let index = 0; index < 8; index += 1) {
        const angle = (index / 8) * Math.PI * 2;
        graphics
          .moveTo(
            centerX + Math.cos(angle) * radius * 0.98,
            centerY + Math.sin(angle) * radius * 0.98
          )
          .lineTo(
            centerX + Math.cos(angle) * radius * 1.32,
            centerY + Math.sin(angle) * radius * 1.32
          );
      }
      graphics.stroke({ width: strokeWidth, color, alpha, cap: "round" });
      return;
    }

    if (icon === "close") {
      graphics
        .moveTo(centerX - size * 0.18, centerY - size * 0.18)
        .lineTo(centerX + size * 0.18, centerY + size * 0.18)
        .moveTo(centerX + size * 0.18, centerY - size * 0.18)
        .lineTo(centerX - size * 0.18, centerY + size * 0.18)
        .stroke({ width: strokeWidth, color, alpha, cap: "round" });
      return;
    }

    if (icon === "music") {
      graphics
        .moveTo(centerX + size * 0.08, centerY - size * 0.26)
        .lineTo(centerX + size * 0.08, centerY + size * 0.14)
        .circle(centerX - size * 0.08, centerY + size * 0.16, size * 0.11)
        .stroke({ width: strokeWidth, color, alpha, cap: "round", join: "round" });
      graphics
        .moveTo(centerX + size * 0.08, centerY - size * 0.24)
        .lineTo(centerX + size * 0.27, centerY - size * 0.18)
        .stroke({ width: strokeWidth, color, alpha, cap: "round" });
      return;
    }

    if (icon === "language") {
      graphics
        .circle(centerX, centerY, radius)
        .stroke({ width: strokeWidth * 0.72, color, alpha })
        .moveTo(centerX - radius, centerY)
        .lineTo(centerX + radius, centerY)
        .moveTo(centerX, centerY - radius)
        .bezierCurveTo(
          centerX - radius * 0.45,
          centerY - radius * 0.35,
          centerX - radius * 0.45,
          centerY + radius * 0.35,
          centerX,
          centerY + radius
        )
        .moveTo(centerX, centerY - radius)
        .bezierCurveTo(
          centerX + radius * 0.45,
          centerY - radius * 0.35,
          centerX + radius * 0.45,
          centerY + radius * 0.35,
          centerX,
          centerY + radius
        )
        .stroke({ width: strokeWidth * 0.72, color, alpha, cap: "round" });
      return;
    }

    if (icon === "haptics") {
      graphics
        .roundRect(
          centerX - size * 0.1,
          centerY - size * 0.24,
          size * 0.2,
          size * 0.48,
          size * 0.04
        )
        .stroke({ width: strokeWidth * 0.72, color, alpha })
        .moveTo(centerX - size * 0.2, centerY - size * 0.16)
        .lineTo(centerX - size * 0.3, centerY - size * 0.08)
        .lineTo(centerX - size * 0.2, centerY)
        .moveTo(centerX - size * 0.2, centerY + size * 0.08)
        .lineTo(centerX - size * 0.3, centerY + size * 0.16)
        .lineTo(centerX - size * 0.2, centerY + size * 0.24)
        .moveTo(centerX + size * 0.2, centerY - size * 0.16)
        .lineTo(centerX + size * 0.3, centerY - size * 0.08)
        .lineTo(centerX + size * 0.2, centerY)
        .moveTo(centerX + size * 0.2, centerY + size * 0.08)
        .lineTo(centerX + size * 0.3, centerY + size * 0.16)
        .lineTo(centerX + size * 0.2, centerY + size * 0.24)
        .stroke({ width: strokeWidth * 0.64, color, alpha, cap: "round", join: "round" });
      return;
    }

    if (icon === "privacy") {
      graphics
        .moveTo(centerX, centerY - size * 0.28)
        .lineTo(centerX + size * 0.24, centerY - size * 0.16)
        .lineTo(centerX + size * 0.2, centerY + size * 0.12)
        .quadraticCurveTo(
          centerX,
          centerY + size * 0.3,
          centerX - size * 0.2,
          centerY + size * 0.12
        )
        .lineTo(centerX - size * 0.24, centerY - size * 0.16)
        .lineTo(centerX, centerY - size * 0.28)
        .stroke({ width: strokeWidth * 0.78, color, alpha, cap: "round", join: "round" });
      graphics
        .moveTo(centerX - size * 0.07, centerY)
        .lineTo(centerX - size * 0.01, centerY + size * 0.08)
        .lineTo(centerX + size * 0.12, centerY - size * 0.08)
        .stroke({ width: strokeWidth * 0.72, color, alpha, cap: "round", join: "round" });
      return;
    }

    if (icon === "policy") {
      graphics
        .moveTo(centerX - size * 0.18, centerY - size * 0.28)
        .lineTo(centerX + size * 0.1, centerY - size * 0.28)
        .lineTo(centerX + size * 0.22, centerY - size * 0.16)
        .lineTo(centerX + size * 0.22, centerY + size * 0.26)
        .lineTo(centerX - size * 0.18, centerY + size * 0.26)
        .lineTo(centerX - size * 0.18, centerY - size * 0.28)
        .moveTo(centerX + size * 0.1, centerY - size * 0.28)
        .lineTo(centerX + size * 0.1, centerY - size * 0.16)
        .lineTo(centerX + size * 0.22, centerY - size * 0.16)
        .moveTo(centerX - size * 0.08, centerY - size * 0.02)
        .lineTo(centerX + size * 0.12, centerY - size * 0.02)
        .moveTo(centerX - size * 0.08, centerY + size * 0.1)
        .lineTo(centerX + size * 0.12, centerY + size * 0.1)
        .stroke({ width: strokeWidth * 0.66, color, alpha, cap: "round", join: "round" });
      return;
    }

    if (icon === "about") {
      graphics
        .circle(centerX, centerY, radius)
        .stroke({ width: strokeWidth * 0.82, color, alpha })
        .circle(centerX, centerY - size * 0.12, size * 0.035)
        .fill({ color, alpha })
        .moveTo(centerX, centerY - size * 0.02)
        .lineTo(centerX, centerY + size * 0.18)
        .stroke({ width: strokeWidth, color, alpha, cap: "round" });
      return;
    }

    if (icon === "hint") {
      graphics
        .circle(centerX - size * 0.06, centerY - size * 0.06, radius)
        .stroke({ width: strokeWidth, color, alpha })
        .moveTo(centerX + size * 0.12, centerY + size * 0.12)
        .lineTo(centerX + size * 0.32, centerY + size * 0.32)
        .stroke({ width: strokeWidth, color, alpha, cap: "round" });
      return;
    }

    if (icon === "next") {
      graphics
        .moveTo(centerX - size * 0.22, centerY)
        .lineTo(centerX + size * 0.24, centerY)
        .lineTo(centerX + size * 0.06, centerY - size * 0.18)
        .moveTo(centerX + size * 0.24, centerY)
        .lineTo(centerX + size * 0.06, centerY + size * 0.18)
        .stroke({ width: strokeWidth, color, alpha, cap: "round", join: "round" });
      return;
    }

    if (icon === "previous") {
      graphics
        .moveTo(centerX + size * 0.2, centerY)
        .lineTo(centerX - size * 0.2, centerY)
        .lineTo(centerX - size * 0.04, centerY - size * 0.16)
        .moveTo(centerX - size * 0.2, centerY)
        .lineTo(centerX - size * 0.04, centerY + size * 0.16)
        .stroke({ width: strokeWidth, color, alpha, cap: "round", join: "round" });
      return;
    }

    if (icon === "sound") {
      graphics
        .moveTo(centerX - size * 0.28, centerY - size * 0.1)
        .lineTo(centerX - size * 0.14, centerY - size * 0.1)
        .lineTo(centerX + size * 0.04, centerY - size * 0.24)
        .lineTo(centerX + size * 0.04, centerY + size * 0.24)
        .lineTo(centerX - size * 0.14, centerY + size * 0.1)
        .lineTo(centerX - size * 0.28, centerY + size * 0.1)
        .lineTo(centerX - size * 0.28, centerY - size * 0.1)
        .stroke({ width: strokeWidth, color, alpha, cap: "round", join: "round" });
      graphics
        .arc(centerX + size * 0.08, centerY, size * 0.2, -Math.PI * 0.28, Math.PI * 0.28)
        .stroke({ width: strokeWidth * 0.82, color, alpha: alpha * 0.82, cap: "round" });
      return;
    }

    if (icon === "palette") {
      const dotRadius = size * 0.07;
      graphics
        .circle(centerX, centerY, size * 0.22)
        .stroke({ width: strokeWidth * 0.82, color, alpha });
      graphics
        .circle(centerX - size * 0.1, centerY - size * 0.05, dotRadius)
        .fill({ color, alpha })
        .circle(centerX + size * 0.08, centerY - size * 0.1, dotRadius)
        .fill({ color, alpha })
        .circle(centerX + size * 0.02, centerY + size * 0.1, dotRadius)
        .fill({ color, alpha });
      return;
    }

    if (icon === "undo") {
      graphics
        .moveTo(centerX + size * 0.22, centerY - size * 0.14)
        .lineTo(centerX - size * 0.12, centerY - size * 0.14)
        .lineTo(centerX - size * 0.12, centerY - size * 0.28)
        .moveTo(centerX - size * 0.12, centerY - size * 0.14)
        .lineTo(centerX - size * 0.28, centerY)
        .lineTo(centerX - size * 0.12, centerY + size * 0.14)
        .stroke({ width: strokeWidth, color, alpha, cap: "round", join: "round" });
      return;
    }

    const resetStartAngle = Math.PI * 0.18;
    graphics
      .moveTo(
        centerX + Math.cos(resetStartAngle) * radius,
        centerY + Math.sin(resetStartAngle) * radius
      )
      .arc(centerX, centerY, radius, resetStartAngle, Math.PI * 1.7)
      .stroke({ width: strokeWidth, color, alpha, cap: "round" })
      .moveTo(centerX + radius * 0.8, centerY - radius * 0.58)
      .lineTo(centerX + radius * 1.1, centerY - radius * 0.98)
      .lineTo(centerX + radius * 1.18, centerY - radius * 0.42)
      .stroke({ width: strokeWidth, color, alpha, cap: "round", join: "round" });
  }

  private drawPolyline(
    graphics: Graphics,
    cells: readonly GridPoint[],
    stroke: { readonly color: number; readonly alpha: number; readonly width: number }
  ): void {
    const first = cells[0];
    if (!first) {
      return;
    }
    const start = gridCellCenter(first, this.metrics.geometry);
    graphics.moveTo(start.x, start.y);
    for (const point of cells.slice(1)) {
      const center = gridCellCenter(point, this.metrics.geometry);
      graphics.lineTo(center.x, center.y);
    }
    graphics.stroke({
      width: stroke.width,
      color: stroke.color,
      alpha: stroke.alpha,
      cap: "round",
      join: "round"
    });
  }

  private findActivePath(snapshot: LevelSnapshot): PathSnapshot | null {
    const activeColor = this.input.activeColor;
    return activeColor ? (snapshot.paths.find((path) => path.color === activeColor) ?? null) : null;
  }

  private resolvePathVisual(color: string) {
    return resolvePathToken(color, this.useColorBlindPalette, this.colorVisionProfile);
  }

  private createGhostGuidePath(): readonly GridPoint[] {
    return this.createGhostGuideSpec()?.path ?? [];
  }

  private createGhostGuideSpec(): GhostGuideSpec | null {
    return createGhostGuideSpecForLevel(this.level.order, this.state);
  }

  private requireFirstLevel(): LevelCatalogEntry {
    const first = getLevelByOrder(this.catalog, 1) ?? this.catalog.entries[0];
    if (!first) {
      throw new Error("GridFlow catalog must contain at least one level.");
    }
    return first;
  }
}

export function formatOriginCounter(
  path: Pick<PathSnapshot, "currentLength" | "isComplete" | "targetLength">
): string {
  if (path.isComplete) {
    return "✓";
  }
  return String(getOriginCounterValue(path));
}

function formatLevelNumber(order: number): string {
  return order < 10 ? `0${order}` : String(order);
}

function resolveNodeLabelColor(path: PathSnapshot, role: "origin" | "target"): number {
  if (path.isComplete) {
    return GRIDFLOW_TOKENS.palette.success;
  }
  const remaining = getOriginCounterValue(path);
  if (role === "origin" && (remaining < 1 || path.isLinked)) {
    return GRIDFLOW_TOKENS.palette.danger;
  }
  return GRIDFLOW_TOKENS.palette.textPrimary;
}

function getOriginCounterValue(path: Pick<PathSnapshot, "currentLength" | "targetLength">): number {
  if (path.currentLength > path.targetLength) {
    return path.targetLength - path.currentLength;
  }
  return path.targetLength - Math.max(0, path.currentLength - 1);
}

function drawNodeShape(
  graphics: Graphics,
  shape: NodeShape,
  x: number,
  y: number,
  radius: number,
  token: { readonly color: number; readonly glowColor: number }
): void {
  const fill = { color: token.color, alpha: 0.72 };
  const stroke = { width: Math.max(1, radius * 0.22), color: token.glowColor, alpha: 0.86 };
  if (shape === "circle") {
    graphics.circle(x, y, radius).fill(fill).stroke(stroke);
    return;
  }
  if (shape === "triangle") {
    graphics
      .regularPoly(x, y, radius, 3, -Math.PI / 2)
      .fill(fill)
      .stroke(stroke);
    return;
  }
  if (shape === "diamond") {
    graphics
      .regularPoly(x, y, radius, 4, Math.PI / 4)
      .fill(fill)
      .stroke(stroke);
    return;
  }
  if (shape === "square") {
    const side = radius * 1.52;
    graphics
      .rect(x - side / 2, y - side / 2, side, side)
      .fill(fill)
      .stroke(stroke);
    return;
  }
  graphics
    .regularPoly(x, y, radius, 6, Math.PI / 6)
    .fill(fill)
    .stroke(stroke);
}

function destroyLayerChildren(layer: Container): void {
  for (const child of layer.removeChildren()) {
    child.destroy({ children: true });
  }
}

function clampCellSize(rawCellSize: number): number {
  if (rawCellSize < GRIDFLOW_TOKENS.layout.minCellSize) {
    return Math.max(1, rawCellSize);
  }
  return Math.min(rawCellSize, GRIDFLOW_TOKENS.layout.maxCellSize);
}

function resolveUiCopy(language: GameLanguage): UiCopy {
  return UI_COPY[resolveEffectiveLanguage(language)];
}

function resolveSettingsCopy(copy: ResolvedGameLanguage | UiCopy): UiCopy {
  return typeof copy === "string" ? UI_COPY[copy] : copy;
}

function resolveSettingsRowMetrics(
  rowCount: number,
  screenHeight: number,
  margin: number
): { readonly height: number; readonly gap: number } {
  const defaultHeight = 44;
  const defaultGap = 5;
  const nonRowHeight = 88;
  const availableHeight = Math.max(360, screenHeight - margin * 2);
  const requiredHeight =
    nonRowHeight + rowCount * defaultHeight + Math.max(0, rowCount - 1) * defaultGap;
  if (requiredHeight <= availableHeight) {
    return { height: defaultHeight, gap: defaultGap };
  }
  const compactGap = 4;
  const compactHeight = Math.max(
    38,
    Math.floor((availableHeight - nonRowHeight - Math.max(0, rowCount - 1) * compactGap) / rowCount)
  );
  return { height: compactHeight, gap: compactGap };
}

export function resolveEffectiveLanguage(
  language: GameLanguage,
  systemLanguage = globalThis.navigator?.language ?? ""
): ResolvedGameLanguage {
  if (language !== "system") {
    return language;
  }
  return resolveSystemLanguage(systemLanguage);
}

export function resolveSystemLanguage(systemLanguage: string): ResolvedGameLanguage {
  const subtags = systemLanguage
    .toLowerCase()
    .split(/[-_]/)
    .filter((subtag) => subtag.length > 0);
  const primary = subtags[0] ?? "";

  if (primary === "zh") {
    return subtags.includes("hant") ||
      subtags.includes("tw") ||
      subtags.includes("hk") ||
      subtags.includes("mo")
      ? "zh-Hant"
      : "zh-Hans";
  }
  if (
    primary === "en" ||
    primary === "es" ||
    primary === "ms" ||
    primary === "th" ||
    primary === "ru"
  ) {
    return primary;
  }
  return "en";
}

export function nextLanguage(language: GameLanguage): GameLanguage {
  const currentIndex = GAME_LANGUAGES.indexOf(language);
  return GAME_LANGUAGES[(currentIndex + 1) % GAME_LANGUAGES.length] ?? "system";
}

export function formatLanguageLabel(language: GameLanguage): string {
  const labels = {
    system: "System",
    en: "English",
    "zh-Hans": "简体中文",
    "zh-Hant": "繁體中文",
    es: "Español",
    ms: "Bahasa Melayu",
    th: "ไทย",
    ru: "Русский"
  } as const satisfies Record<GameLanguage, string>;
  return labels[language];
}

function readLanguagePreference(): GameLanguage {
  try {
    const stored = globalThis.localStorage?.getItem(GRIDFLOW_LANGUAGE_STORAGE_KEY);
    return isGameLanguage(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

function writeLanguagePreference(language: GameLanguage): void {
  try {
    globalThis.localStorage?.setItem(GRIDFLOW_LANGUAGE_STORAGE_KEY, language);
  } catch {
    return;
  }
}

function resolvePreferenceStorage(
  storage: PreferenceStorage | null | undefined
): PreferenceStorage | null {
  return storage === undefined ? getDefaultPreferenceStorage() : storage;
}

function getDefaultPreferenceStorage(): PreferenceStorage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function getGlobalOpen(): ((url: string) => unknown) | null {
  try {
    return typeof globalThis.open === "function"
      ? (url: string) => globalThis.open?.(url, "_blank", "noopener,noreferrer")
      : null;
  } catch {
    return null;
  }
}

export function isGameLanguage(value: string | null | undefined): value is GameLanguage {
  return GAME_LANGUAGES.includes(value as GameLanguage);
}

function readReducedMotionPreference(): boolean {
  return globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

function createStuckOptions(options: GridFlowGameOptions): StuckDetectorOptions {
  return {
    ...(options.hintStuckThresholdMs !== undefined
      ? { thresholdMs: options.hintStuckThresholdMs }
      : {}),
    ...(options.forceHintAvailable !== undefined
      ? { forceAvailable: options.forceHintAvailable }
      : {})
  };
}

function createResizeObserver(callback: () => void): ResizeObserver | null {
  return typeof ResizeObserver === "undefined" ? null : new ResizeObserver(callback);
}

const SVG_NAMESPACE = "http://www.w3.org/2000/svg";

function createGuideOverlayElement(): HTMLDivElement {
  const element = document.createElement("div");
  element.dataset.gridflowGuideOverlay = "true";
  element.style.position = "absolute";
  element.style.inset = "0";
  element.style.pointerEvents = "none";
  element.style.overflow = "hidden";
  element.style.zIndex = "999";
  return element;
}

function createSvgElement<K extends keyof SVGElementTagNameMap>(
  tagName: K
): SVGElementTagNameMap[K] {
  return document.createElementNS(SVG_NAMESPACE, tagName);
}

function appendSvgPolyline(
  svg: SVGSVGElement,
  cells: readonly GridPoint[],
  geometry: BoardGeometry,
  color: string,
  opacity: number,
  width: number
): void {
  if (cells.length < 2) {
    return;
  }
  const polyline = createSvgElement("polyline");
  setSvgAttributes(polyline, {
    points: cells.map((cell) => formatSvgPoint(gridCellCenter(cell, geometry))).join(" "),
    fill: "none",
    stroke: color,
    "stroke-opacity": `${clamp01(opacity)}`,
    "stroke-width": `${width}`,
    "stroke-linecap": "round",
    "stroke-linejoin": "round"
  });
  svg.appendChild(polyline);
}

function appendSvgWaypoints(
  svg: SVGSVGElement,
  cells: readonly GridPoint[],
  geometry: BoardGeometry,
  color: string,
  opacity: number
): void {
  const radius = geometry.cellSize * 0.095;
  for (const cell of cells) {
    const center = gridCellCenter(cell, geometry);
    appendSvgFilledCircle(svg, center, radius * 1.9, color, opacity * 0.18);
    appendSvgFilledCircle(svg, center, radius, color, opacity);
  }
}

function appendSvgCircle(
  svg: SVGSVGElement,
  center: ScreenPoint,
  radius: number,
  color: string,
  opacity: number,
  fill: string
): void {
  const circle = createSvgElement("circle");
  setSvgAttributes(circle, {
    cx: `${center.x}`,
    cy: `${center.y}`,
    r: `${radius}`,
    fill,
    stroke: color,
    "stroke-opacity": `${clamp01(opacity)}`,
    "stroke-width": `${Math.max(2, radius * 0.12)}`
  });
  svg.appendChild(circle);
}

function appendSvgFilledCircle(
  svg: SVGSVGElement,
  center: ScreenPoint,
  radius: number,
  color: string,
  opacity: number
): void {
  const circle = createSvgElement("circle");
  setSvgAttributes(circle, {
    cx: `${center.x}`,
    cy: `${center.y}`,
    r: `${radius}`,
    fill: color,
    opacity: `${clamp01(opacity)}`
  });
  svg.appendChild(circle);
}

function setSvgAttributes(element: SVGElement, attributes: Record<string, string>): void {
  for (const [key, value] of Object.entries(attributes)) {
    element.setAttribute(key, value);
  }
}

function formatSvgPoint(point: ScreenPoint): string {
  return `${point.x},${point.y}`;
}

function numberToCssColor(color: number): string {
  return `#${color.toString(16).padStart(6, "0")}`;
}

function distanceBetween(left: ScreenPoint, right: ScreenPoint): number {
  return Math.hypot(left.x - right.x, left.y - right.y);
}

function easeOutCubic(value: number): number {
  const clamped = clamp01(value);
  return 1 - Math.pow(1 - clamped, 3);
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.min(1, value));
}

function hashParticleSeed(pathColor: string, x: number, y: number, index: number): number {
  let hash = 2166136261;
  for (const char of pathColor) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  hash ^= x + 31 * y + 131 * index;
  hash = Math.imul(hash, 16777619);
  return ((hash >>> 0) % 1000) / 1000;
}

function capturePointer(canvas: HTMLCanvasElement, pointerId: number): void {
  try {
    canvas.setPointerCapture(pointerId);
  } catch {
    return;
  }
}

function releasePointer(canvas: HTMLCanvasElement, pointerId: number): void {
  try {
    canvas.releasePointerCapture(pointerId);
  } catch {
    return;
  }
}
